import React from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { getLocationReturnPath, useNavigateBack } from '../../../hooks/useNavigateBack';
import useSWR, { useSWRConfig } from 'swr';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { cn } from '../../../lib/utils';
import { fetcher } from '../../../services/fetcher';
import { normalizePaginatedListResponse } from '../../../services/paginatedList';
import {
  createSpareRequisition,
  createSpareSupplierEnquiryFromRequisitionMulti,
  createSpareDeliveryFromPurchaseOrder,
  createSparePurchaseOrderFromQuotation,
  createSpareSupplierQuotationFromEnquiry,
  deleteSpareQuotationEvaluation,
  deleteSpareSupplierEnquiry,
  deleteSpareSupplierQuotation,
  deleteSpareDelivery,
  deleteSpareForwarding,
  deleteSpareInvoice,
  deleteSparePurchaseOrder,
  unlinkSpareRequisitionFromCase,
  updateSpareRequisition,
} from '../../../services/mutations';
import { formatDate as formatDateGlobal, formatDateTime as formatDateTimeGlobal } from '../../../config/appConfig';
import { portalOfferStatusCompactLabel, type PortalOfferStatus } from '../portalOfferDisplay';
import { copyTextToClipboard } from '../../../utils/copyToClipboard';
import {
  Button,
  Card,
  ConfirmationDialog,
  Accordion,
  AccordionItem,
  ChevronLeftIcon,
  Badge,
  CopyIcon,
  DocumentTextIcon,
  EyeIcon,
  InfoIcon,
  Label,
  MultiSelect,
  RefreshIcon,
  SegmentedControl,
  Select,
  Switch,
  TableIcon,
  Textarea,
  ViewColumnsIcon,
  XIcon,
} from '../../../components/ui';
import { Modal, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '../../../components/ui/Modal';
import { Z_INDEX, zStyle } from '../../../lib/zLayers';
import { moduleConfig } from './config';
import { moduleConfig as supplierEnquiryConfig } from '../supplier-enquiries/supplierEnquiriesModuleConfig';
import { moduleConfig as supplierQuotationConfig } from '../supplier-quotations/supplierQuotationsModuleConfig';
import { moduleConfig as purchaseOrdersConfig } from '../spare-purchase-orders/sparePurchaseOrdersModuleConfig';
import { moduleConfig as supplyForwardingsConfig } from '../spare-forwardings/spareForwardingsModuleConfig';
import { moduleConfig as supplyDeliveriesConfig } from '../spare-deliveries/spareDeliveriesModuleConfig';
import { moduleConfig as supplyInvoicesConfig } from '../spare-invoices/spareInvoicesModuleConfig';
import { moduleConfig as quotationEvaluationsConfig } from '../quotation-evaluations/quotationEvaluationsModuleConfig';
import { moduleConfig as requisitionsModuleConfig } from '../requisitions/requisitionsModuleConfig';
import type { SpareRequisition } from '../requisitions/useData';
type CaseViewPayload = {
  spareCase: {
    id: number;
    ref?: string | null;
    caseDate?: string | null;
    eta?: string | null;
    description?: string | null;
    notes?: string | null;
    vessel?: { id: number; vesselName?: string | null } | null;
    port?: { name?: string | null; code?: string | null } | null;
    status?: { name?: string | null } | null;
  };
  requisitions: {
    id: number;
    ref?: string | null;
    reqDate?: string | null;
    saveStatus?: string | null;
    vessel?: { vesselName?: string | null } | null;
  }[];
  enquiries: {
    id: number;
    ref?: string | null;
    enqDate?: string | null;
    requisitionId?: number | null;
    updatedAt?: string | null;
    supplier?: { name?: string | null; code?: string | null } | null;
  }[];
  quotations: {
    id: number;
    ref?: string | null;
    quoDate?: string | null;
    enqId: number;
    requisitionId?: number | null;
    rate?: string | null;
    updatedAt?: string | null;
    currency?: { id: number; code: string; name: string; symbol?: string | null } | null;
    supplier?: { name?: string | null; code?: string | null } | null;
    /** Sum of line totals + expenses in quotation currency */
    totalLocal?: string | null;
    /** Local grand total × rate when rate is set */
    totalUsd?: string | null;
    portalOfferStatus?: PortalOfferStatus;
    rfqPortalLastOpenedAt?: string | null;
    rfqPortalLastSavedAt?: string | null;
    /** Portal offer submitted at (enquiry.rfqPublicSubmittedAt). */
    rfqPublicSubmittedAt?: string | null;
  }[];
  evaluations: {
    id: number;
    ref?: string | null;
    evaluationDate?: string | null;
    quotation1Id?: number | null;
    quotation2Id?: number | null;
    quotation3Id?: number | null;
    quotation4Id?: number | null;
    quotation5Id?: number | null;
  }[];
  purchaseOrders: {
    id: number;
    ref?: string | null;
    orderDate?: string | null;
    quotationId: number;
    exchangeRate?: string | null;
    updatedAt?: string | null;
    currency?: { id: number; code: string; name: string; symbol?: string | null } | null;
    totalLocal?: string | null;
    totalUsd?: string | null;
    supplier?: { name?: string | null; code?: string | null } | null;
    forwarding?: { id: number; ref?: string | null } | null;
  }[];
  /** Logistics forwarding rows for this case (after PO, before delivery). */
  forwardings?: {
    id: number;
    ref?: string | null;
    purchaseOrderId?: number | null;
    etaToPort?: string | null;
    createdAt: string;
    costUsd?: string | null;
    forwarder?: { id: number; name?: string | null; code?: string | null } | null;
    port?: { id: number; name?: string | null; code?: string | null } | null;
    status?: { id: number; name?: string | null; code?: string | null } | null;
    priority?: { id: number; name?: string | null; code?: string | null } | null;
    warehouseLocation?: { id: number; name?: string | null; code?: string | null } | null;
    warehouseArrivalDate?: string | null;
    warehouseDepartureDate?: string | null;
    warehouseInvoiceNo?: string | null;
    warehouseCostUsd?: string | null;
    subAgentArrivalDate?: string | null;
    subAgentDeliveryDate?: string | null;
    subAgentInvoiceNo?: string | null;
    subAgentCostUsd?: string | null;
    vesselAgentArrivalDate?: string | null;
    vesselAgentDeliveryDate?: string | null;
    vesselAgentInvoiceNo?: string | null;
    vesselAgentCostUsd?: string | null;
    deliveryCount: number;
  }[];
  deliveries: {
    id: number;
    ref?: string | null;
    dlvDate?: string | null;
    deliveryDate?: string | null;
    purchaseOrderId?: number | null;
    supplier?: { name?: string | null; code?: string | null } | null;
  }[];
  invoices: {
    id: number;
    ref?: string | null;
    invDate?: string | null;
    purchaseOrderId?: number | null;
    deliveryId?: number | null;
    exchangeRate?: string | null;
    updatedAt?: string | null;
    currency?: { id: number; code: string; name: string; symbol?: string | null } | null;
    totalLocal?: string | null;
    totalUsd?: string | null;
    supplier?: { name?: string | null; code?: string | null } | null;
  }[];
  itemsActivity?: {
    enquiryColumns: {
      id: number;
      ref: string | null;
      supplier: { id: number; name: string | null; code: string | null };
    }[];
    quotationColumns: {
      id: number;
      ref: string | null;
      enqId: number;
      supplier: { id: number; name: string | null; code: string | null };
      currencyCode: string | null;
      currencySymbol: string | null;
      portalOfferStatus: PortalOfferStatus;
      rfqPortalLastOpenedAt: string | null;
      rfqPortalLastSavedAt: string | null;
      rfqPublicSubmittedAt: string | null;
    }[];
    purchaseOrderColumns: {
      id: number;
      ref: string | null;
      supplierId: number;
      quotationId: number;
      currencyCode: string | null;
      currencySymbol: string | null;
      supplier: { id: number; name: string | null; code: string | null };
    }[];
    deliveryColumns: {
      id: number;
      ref: string | null;
      purchaseOrderId: number | null;
      allLinesReceived: boolean;
      lineCount: number;
    }[];
    invoiceColumns: {
      id: number;
      ref: string | null;
      supplierId: number;
      purchaseOrderId: number | null;
      deliveryId: number | null;
      currencyCode: string | null;
      currencySymbol: string | null;
      supplier: { id: number; name: string | null; code: string | null };
    }[];
    rows: {
      rowKey: string;
      itemId: number | null;
      itemVcode: string | null;
      itemDescr: string | null;
      unit: { id: number; ucode: string; descr: string } | null;
      reqQty: number;
      enquiries: { hasLine: boolean; qty: number | null }[];
      quotations: {
        hasLine: boolean;
        qty: number | null;
        unitPrice: string | null;
        lineTotal: string | null;
        lineTotalUsd: string | null;
        currencyCode: string | null;
        /** Quotation line vs linked supplier enquiry (RFQ): quantities only (enquiry has no price). */
        rfqLineMatch:
          | { code: 'no_rfq' }
          | { code: 'rfq_not_in_case' }
          | { code: 'n_a' }
          | { code: 'ok' }
          | { code: 'mismatch'; fields: 'qty'[] }
          | { code: 'extra_on_quotation' }
          | { code: 'missing_on_quotation' };
      }[];
      purchaseOrders: {
        hasLine: boolean;
        qty: number | null;
        unitPrice: string | null;
        lineTotal: string | null;
        lineTotalUsd: string | null;
        currencyCode: string | null;
      }[];
      deliveries: {
        hasLine: boolean;
        deliveryQty: number | null;
        purchaseOrderQty: number | null;
        isDelivered: boolean | null;
      }[];
      invoices: {
        hasLine: boolean;
        qty: number | null;
        unitPrice: string | null;
        lineTotal: string | null;
        lineTotalUsd: string | null;
        currencyCode: string | null;
        /** This invoice line compared to the line on the linked purchase order (if any). */
        orderLineMatch:
          | { code: 'no_po' }
          | { code: 'po_not_in_case' }
          | { code: 'n_a' }
          | { code: 'ok' }
          | { code: 'mismatch'; fields: Array<'qty' | 'unitPrice' | 'lineTotal'> }
          | { code: 'extra_on_invoice' }
          | { code: 'missing_on_invoice' };
      }[];
    }[];
  };
};

type CaseViewTab = 'case' | 'items';

const getTodayLocalDateString = (): string => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Requisitions linked to a purchase order on this case (via quotation → enquiry / quotation.requisitionId). */
function buildRequisitionIdsWithPurchaseOrder(data: CaseViewPayload): Set<number> {
  const quoById = new Map(data.quotations.map((q) => [q.id, q]));
  const enqById = new Map(data.enquiries.map((e) => [e.id, e]));
  const ids = new Set<number>();
  for (const po of data.purchaseOrders) {
    const q = quoById.get(po.quotationId);
    if (!q) continue;
    if (q.requisitionId != null) ids.add(q.requisitionId);
    const enq = enqById.get(q.enqId);
    if (enq?.requisitionId != null) ids.add(enq.requisitionId);
  }
  return ids;
}

type TableRow = {
  kind: string;
  id: number;
  ref: string;
  dateStr: string;
  sortKey: string;
  detail: string;
  href: string | null;
  /** Extra line (e.g. last updated) under Details */
  contextExtra?: string;
  /** Requisition rows only: save status for badge display */
  requisitionSaveStatus?: string | null;
  /** True when a shared Approvals PENDING request exists for this pipeline record. */
  pendingApproval?: boolean;
  /** Delivery rows: linked PO for “Create invoice” (when present). */
  deliveryPurchaseOrderId?: number | null;
  /** Delivery: open new requisition with no case when lines are pending or qty ≠ PO (from items activity). */
  newRequisitionOutsideCaseHref?: string | null;
  /** Quotation activity row: structured details (totals after rate). */
  quotationDetail?: {
    supplierLabel: string;
    currencyLabel: string | null;
    currencyCode: string | null;
    currencySymbol: string | null;
    rate: string | null;
    totalLocal: string | null;
    totalUsd: string | null;
    updatedDateStr?: string | null;
    portalOfferStatus?: PortalOfferStatus;
    rfqPortalLastOpenedAt?: string | null;
    rfqPortalLastSavedAt?: string | null;
    rfqPublicSubmittedAt?: string | null;
  };
  purchaseOrderDetail?: {
    supplierLabel: string;
    quotationId: number;
    /** Pill label — quotation ref */
    quotationLinkLabel: string;
    currencyLabel: string | null;
    currencyCode: string | null;
    currencySymbol: string | null;
    rate: string | null;
    totalLocal: string | null;
    totalUsd: string | null;
    updatedDateStr?: string | null;
  };
  invoiceDetail?: {
    supplierLabel: string;
    purchaseOrderId: number | null;
    deliveryId: number | null;
    purchaseOrderLinkLabel: string | null;
    deliveryLinkLabel: string | null;
    currencyLabel: string | null;
    currencyCode: string | null;
    currencySymbol: string | null;
    rate: string | null;
    totalLocal: string | null;
    totalUsd: string | null;
    updatedDateStr?: string | null;
  };
  enquiryDetail?: {
    supplierLabel: string;
    requisitionId: number | null;
    /** Pill label — user-facing ref, never raw DB id */
    requisitionLinkLabel: string | null;
    updatedDateStr: string | null;
  };
  deliveryDetail?: {
    supplierLabel: string;
    purchaseOrderId: number | null;
    purchaseOrderLinkLabel: string | null;
    hasLineIssues: boolean;
  };
  evaluationDetail?: {
    quotations: Array<{ id: number; refLabel: string; supplierLabel: string }>;
  };
  forwardingDetail?: {
    forwarderLabel: string;
    purchaseOrderId: number | null;
    purchaseOrderLinkLabel: string | null;
    portLabel: string | null;
    statusLabel: string | null;
    priorityLabel: string | null;
    warehouseLocationLabel: string | null;
    costUsdStr: string | null;
    etaDateStr: string | null;
    deliveryCount: number;
  };
};

type ViewMode = 'flat' | 'grouped';

const kindOrder: Record<string, number> = {
  Requisition: 0,
  'Supplier enquiry': 1,
  Quotation: 2,
  Evaluation: 3,
  'Purchase order': 4,
  Forwarding: 5,
  Delivery: 6,
  Invoice: 7,
};

/** Multi-line title for supplier portal quotation status (hover / focus on info control). */
function quotationPortalStatusTooltip(
  portalOfferStatus: PortalOfferStatus,
  rfqPortalLastOpenedAt: string | null | undefined,
  rfqPortalLastSavedAt: string | null | undefined,
  rfqPublicSubmittedAt: string | null | undefined,
): string {
  const dt = (iso: string | null | undefined) => (iso ? formatDateTimeGlobal(iso) : '—');
  const headline =
    portalOfferStatus === 'submitted'
      ? 'Submitted — supplier finalized the offer via the public RFQ link.'
      : 'Draft — not yet submitted via the RFQ link. Portal timestamps below show supplier activity.';
  return [
    headline,
    '',
    `Last login: ${dt(rfqPortalLastOpenedAt)}`,
    `Last saved: ${dt(rfqPortalLastSavedAt)}`,
    `Last submitted: ${dt(rfqPublicSubmittedAt)}`,
  ].join('\n');
}

function deliveryHasLineIssuesForCaseView(
  itemsActivity: CaseViewPayload['itemsActivity'] | undefined,
  deliveryId: number,
): boolean {
  if (!itemsActivity?.rows?.length) return false;
  const colIdx = itemsActivity.deliveryColumns.findIndex((c) => c.id === deliveryId);
  if (colIdx < 0) return false;
  for (const r of itemsActivity.rows) {
    const cell = r.deliveries[colIdx];
    if (!cell?.hasLine) continue;
    if (cell.isDelivered === false) return true;
    const poQ = cell.purchaseOrderQty;
    const dlvQ = cell.deliveryQty;
    if (poQ != null && dlvQ != null && poQ !== dlvQ) return true;
  }
  return false;
}

function deliveryReceiptMetrics(
  itemsActivity: CaseViewPayload['itemsActivity'] | undefined,
  deliveryId: number,
): { linkedLines: number; pendingReceipt: number } {
  if (!itemsActivity?.rows?.length) return { linkedLines: 0, pendingReceipt: 0 };
  const colIdx = itemsActivity.deliveryColumns.findIndex((c) => c.id === deliveryId);
  if (colIdx < 0) return { linkedLines: 0, pendingReceipt: 0 };
  let linkedLines = 0;
  let pendingReceipt = 0;
  for (const r of itemsActivity.rows) {
    const cell = r.deliveries[colIdx];
    if (!cell?.hasLine) continue;
    linkedLines++;
    if (cell.isDelivered === false) pendingReceipt++;
  }
  return { linkedLines, pendingReceipt };
}

function formatRequisitionSaveStatusLabel(saveStatus: string): string {
  const u = saveStatus.trim().toUpperCase();
  if (u === 'SAVED') return 'Saved';
  if (u === 'DRAFT') return 'Draft';
  return saveStatus;
}

function fmtActivityMoney(raw: string | null | undefined): string {
  if (raw == null || raw === '') return '—';
  const n = Number(raw);
  if (!Number.isFinite(n)) return String(raw);
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

/** Document has a usable FX figure (rate field populated and numeric). */
function activityFxRatePresent(rate: string | null | undefined): boolean {
  if (rate == null || String(rate).trim() === '') return false;
  const n = Number(rate);
  return Number.isFinite(n);
}

/** Clickable related-record pills (case activity Details). */
const ACTIVITY_REL_PILL =
  'inline-flex max-w-full items-center rounded-md border px-2 py-0.5 text-[11px] font-semibold tracking-wide transition-colors border-indigo-200/90 bg-white/80 text-indigo-700 hover:bg-indigo-50 visited:text-indigo-700 dark:border-indigo-800/80 dark:bg-white/[0.04] dark:text-indigo-300 dark:hover:bg-indigo-950/50 dark:visited:text-indigo-300';

/** Left accent on Case activity Details cells — matches pipeline stage. */
function activityDetailsRailClass(kind: string): string {
  switch (kind) {
    case 'Requisition':
      return 'border-l-[3px] border-indigo-400 pl-3 dark:border-indigo-500';
    case 'Supplier enquiry':
      return 'border-l-[3px] border-violet-400 pl-3 dark:border-violet-500';
    case 'Quotation':
      return 'border-l-[3px] border-amber-400 pl-3 dark:border-amber-500';
    case 'Evaluation':
      return 'border-l-[3px] border-slate-400 pl-3 dark:border-slate-500';
    case 'Purchase order':
      return 'border-l-[3px] border-emerald-400 pl-3 dark:border-emerald-500';
    case 'Forwarding':
      return 'border-l-[3px] border-teal-400 pl-3 dark:border-teal-500';
    case 'Delivery':
      return 'border-l-[3px] border-sky-400 pl-3 dark:border-sky-500';
    case 'Invoice':
      return 'border-l-[3px] border-rose-400 pl-3 dark:border-rose-500';
    default:
      return 'border-l-[3px] border-gray-300 pl-3 dark:border-gray-600';
  }
}

const ACTIVITY_FINANCIAL_EDIT_LINK =
  'whitespace-nowrap text-[11px] font-semibold text-indigo-600 underline-offset-2 hover:underline dark:text-indigo-400';

/** Shared column widths for Stages layout tables (`table-fixed`) — identical across sections. */
const caseActivityGroupedColgroup = (
  <colgroup>
    <col style={{ width: '20%' }} />
    <col style={{ width: '13%' }} />
    <col style={{ width: '10%' }} />
    <col style={{ width: '49%' }} />
    <col style={{ width: '8%' }} />
  </colgroup>
);

const kindBadgeClass = (kind: string) => {
  const base = 'inline-flex max-w-full items-center rounded-full px-2.5 py-0.5 text-xs font-medium';
  switch (kind) {
    case 'Requisition':
      return `${base} bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-200`;
    case 'Supplier enquiry':
      return `${base} bg-violet-100 text-violet-800 dark:bg-violet-900/50 dark:text-violet-200`;
    case 'Quotation':
      return `${base} bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100`;
    case 'Evaluation':
      return `${base} bg-slate-200 text-slate-800 dark:bg-slate-600 dark:text-slate-100`;
    case 'Purchase order':
      return `${base} bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100`;
    case 'Forwarding':
      return `${base} bg-teal-100 text-teal-900 dark:bg-teal-900/45 dark:text-teal-100`;
    case 'Delivery':
      return `${base} bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-100`;
    case 'Invoice':
      return `${base} bg-rose-100 text-rose-900 dark:bg-rose-900/40 dark:text-rose-100`;
    default:
      return `${base} bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-100`;
  }
};

/** Ordered quotation slots on an evaluation → rows for case activity Details (refs + suppliers). */
function evaluationLinkedQuotations(
  ev: CaseViewPayload['evaluations'][number],
  quoById: Map<number, CaseViewPayload['quotations'][number]>,
): Array<{ id: number; refLabel: string; supplierLabel: string }> {
  const slotIds = [
    ev.quotation1Id,
    ev.quotation2Id,
    ev.quotation3Id,
    ev.quotation4Id,
    ev.quotation5Id,
  ].filter((x): x is number => x != null && Number.isFinite(Number(x)));
  const out: Array<{ id: number; refLabel: string; supplierLabel: string }> = [];
  const seen = new Set<number>();
  for (const qid of slotIds) {
    if (seen.has(qid)) continue;
    seen.add(qid);
    const q = quoById.get(qid);
    const sup = q?.supplier;
    const supLabel = sup
      ? sup.code
        ? `${sup.name ?? ''} (${sup.code})`.trim()
        : (sup.name ?? '').trim()
      : '';
    out.push({
      id: qid,
      refLabel: q?.ref?.trim() || `Q-${qid}`,
      supplierLabel: supLabel || '—',
    });
  }
  return out;
}

const buildRows = (data: CaseViewPayload | undefined): TableRow[] => {
  if (!data) return [];
  const rows: TableRow[] = [];
  const d = (s?: string | null) => (s ? formatDateGlobal(s) : '');
  const reqById = new Map(data.requisitions.map((r) => [r.id, r]));
  const quoById = new Map(data.quotations.map((q) => [q.id, q]));
  const poById = new Map(data.purchaseOrders.map((p) => [p.id, p]));
  const dlvById = new Map(data.deliveries.map((dlv) => [dlv.id, dlv]));
  for (const r of data.requisitions) {
    const date = r.reqDate || null;
    rows.push({
      kind: 'Requisition',
      id: r.id,
      ref: r.ref || `R-${r.id}`,
      dateStr: d(date),
      sortKey: date || '',
      detail: '',
      requisitionSaveStatus: r.saveStatus ?? null,
      href: `/spares/requisitions/${r.id}`,
    });
  }
  for (const e of data.enquiries) {
    const date = e.enqDate || null;
    const sup = e.supplier;
    const supLabel = sup ? (sup.code ? `${sup.name ?? ''} (${sup.code})` : (sup.name ?? '')) : '';
    const upd = e.updatedAt ? d(e.updatedAt) : null;
    const refLabel = e.ref || `ENQ-${e.id}`;
    const linkedReq = e.requisitionId != null ? reqById.get(e.requisitionId) : undefined;
    const reqDetailSegment =
      e.requisitionId != null
        ? linkedReq?.ref?.trim()
          ? `Requisition ${linkedReq.ref}`
          : 'Linked requisition'
        : null;
    const identityParts = [`Supplier: ${supLabel}`, refLabel, reqDetailSegment, upd ? `Updated ${upd}` : null].filter(
      (x): x is string => Boolean(x),
    );
    rows.push({
      kind: 'Supplier enquiry',
      id: e.id,
      ref: refLabel,
      dateStr: d(date),
      sortKey: date || '',
      detail: identityParts.join(' · '),
      enquiryDetail: {
        supplierLabel: supLabel.trim() || '—',
        requisitionId: e.requisitionId ?? null,
        requisitionLinkLabel:
          e.requisitionId != null ? linkedReq?.ref?.trim() || 'Requisition' : null,
        updatedDateStr: upd,
      },
      href: `/spares/supplier-enquiries/${e.id}`,
    });
  }
  for (const q of data.quotations) {
    const date = q.quoDate || null;
    const sup = q.supplier;
    const supLabel = sup ? (sup.code ? `${sup.name ?? ''} (${sup.code})` : (sup.name ?? '')) : '';
    const upd = q.updatedAt ? d(q.updatedAt) : null;
    const cur = q.currency;
    const curLabel = cur ? (cur.symbol ? `${cur.code} (${cur.symbol})` : cur.code) : null;
    const quoRef = q.ref || `Q-${q.id}`;
    const portalStatus = q.portalOfferStatus ?? 'pending';
    const portalSummary =
      portalStatus === 'submitted' ? 'Portal: offer submitted' : 'Portal: draft (not submitted)';
    const portalTimes = [
      q.rfqPortalLastOpenedAt ? `Login ${formatDateTimeGlobal(q.rfqPortalLastOpenedAt)}` : null,
      q.rfqPortalLastSavedAt ? `Saved ${formatDateTimeGlobal(q.rfqPortalLastSavedAt)}` : null,
      q.rfqPublicSubmittedAt ? `Submitted ${formatDateTimeGlobal(q.rfqPublicSubmittedAt)}` : null,
    ].filter(Boolean);
    const detailParts = [
      `Supplier: ${supLabel}`,
      quoRef,
      upd ? `Updated ${upd}` : null,
      curLabel ?? null,
      q.rate != null ? `Rate ${q.rate}` : null,
      q.totalLocal != null ? `Total ${q.totalLocal}${cur?.code ? ` ${cur.code}` : ''}` : null,
      q.totalUsd != null ? `Total USD ${q.totalUsd}` : null,
      [portalSummary, ...portalTimes].filter(Boolean).join(' · ') || null,
    ].filter((x): x is string => Boolean(x));
    rows.push({
      kind: 'Quotation',
      id: q.id,
      ref: quoRef,
      dateStr: d(date),
      sortKey: date || '',
      detail: detailParts.join(' · '),
      quotationDetail: {
        supplierLabel: supLabel.trim() || '—',
        currencyLabel: curLabel,
        currencyCode: cur?.code ?? null,
        currencySymbol: cur?.symbol ?? null,
        rate: q.rate ?? null,
        totalLocal: q.totalLocal ?? null,
        totalUsd: q.totalUsd ?? null,
        updatedDateStr: upd,
        portalOfferStatus: portalStatus,
        rfqPortalLastOpenedAt: q.rfqPortalLastOpenedAt ?? null,
        rfqPortalLastSavedAt: q.rfqPortalLastSavedAt ?? null,
        rfqPublicSubmittedAt: q.rfqPublicSubmittedAt ?? null,
      },
      href: `/spares/supplier-quotations/${q.id}`,
    });
  }
  for (const ev of data.evaluations) {
    const date = ev.evaluationDate || null;
    const linked = evaluationLinkedQuotations(ev, quoById);
    const evRef = ev.ref || `EV-${ev.id}`;
    const detailParts = linked.map((x) => `${x.refLabel} (${x.supplierLabel})`);
    const detail =
      linked.length > 0
        ? `${linked.length} quotation${linked.length === 1 ? '' : 's'}${detailParts.length ? `: ${detailParts.join(' · ')}` : ''}`
        : 'No quotations linked on evaluation';
    rows.push({
      kind: 'Evaluation',
      id: ev.id,
      ref: evRef,
      dateStr: d(date),
      sortKey: date || '',
      detail,
      evaluationDetail: { quotations: linked },
      href: `/spares/quotation-evaluations/${ev.id}`,
    });
  }
  for (const p of data.purchaseOrders) {
    const date = p.orderDate || null;
    const sup = p.supplier;
    const supLabel = sup ? (sup.code ? `${sup.name ?? ''} (${sup.code})` : (sup.name ?? '')) : '';
    const cur = p.currency;
    const curLabel = cur ? (cur.symbol ? `${cur.code} (${cur.symbol})` : cur.code) : null;
    const poRef = p.ref || `PO-${p.id}`;
    const poUpd = p.updatedAt ? d(p.updatedAt) : null;
    const linkedQuo = quoById.get(p.quotationId);
    const quoDetailSegment = linkedQuo?.ref?.trim()
      ? `Quotation ${linkedQuo.ref}`
      : linkedQuo != null
        ? 'Linked quotation'
        : null;
    const detailParts = [
      `Supplier: ${supLabel}`,
      poRef,
      quoDetailSegment,
      poUpd ? `Updated ${poUpd}` : null,
      curLabel ?? null,
      p.exchangeRate != null ? `Rate ${p.exchangeRate}` : null,
      p.totalLocal != null ? `Total ${p.totalLocal}${cur?.code ? ` ${cur.code}` : ''}` : null,
      p.totalUsd != null ? `Total USD ${p.totalUsd}` : null,
    ].filter((x): x is string => Boolean(x));
    rows.push({
      kind: 'Purchase order',
      id: p.id,
      ref: poRef,
      dateStr: d(date),
      sortKey: date || '',
      detail: detailParts.join(' · '),
      purchaseOrderDetail: {
        supplierLabel: supLabel.trim() || '—',
        quotationId: p.quotationId,
        quotationLinkLabel: linkedQuo?.ref?.trim() || 'Quotation',
        currencyLabel: curLabel,
        currencyCode: cur?.code ?? null,
        currencySymbol: cur?.symbol ?? null,
        rate: p.exchangeRate ?? null,
        totalLocal: p.totalLocal ?? null,
        totalUsd: p.totalUsd ?? null,
        updatedDateStr: poUpd,
      },
      href: purchaseOrdersConfig.module.route.detail(p.id),
    });
  }
  for (const fwd of data.forwardings ?? []) {
    const eta = fwd.etaToPort ?? null;
    const createdDay = fwd.createdAt ? fwd.createdAt.slice(0, 10) : null;
    const sortDate = eta || createdDay || '';
    const fwdRef = fwd.ref?.trim() || `FWD-${fwd.id}`;
    const linkedPoFwd = fwd.purchaseOrderId != null ? poById.get(fwd.purchaseOrderId) : undefined;
    const fr = fwd.forwarder;
    const forwarderLabel = fr
      ? fr.code
        ? `${fr.name ?? ''} (${fr.code})`.trim()
        : (fr.name ?? '').trim()
      : '';
    const port = fwd.port;
    const portLabel = port ? [port.name, port.code].filter(Boolean).join(' · ') : '';
    const st = fwd.status;
    const statusLabel = (st?.name ?? '').trim() || (st?.code ?? '').trim() || '';
    const pr = fwd.priority;
    const priorityLabel = pr ? ((pr.name ?? '').trim() || (pr.code ?? '').trim() || '') : '';
    const whLoc = fwd.warehouseLocation;
    const warehouseLocationLabel = whLoc
      ? [whLoc.name, whLoc.code].filter(Boolean).join(' · ')
      : '';
    const costUsdStr =
      fwd.costUsd != null && String(fwd.costUsd).trim() !== ''
        ? fmtActivityMoney(String(fwd.costUsd))
        : '';
    const poSeg =
      fwd.purchaseOrderId != null
        ? linkedPoFwd?.ref?.trim()
          ? `Order ${linkedPoFwd.ref}`
          : 'Linked order'
        : 'No purchase order';
    const detailParts = [
      forwarderLabel ? `Forwarder: ${forwarderLabel}` : null,
      portLabel ? `Port: ${portLabel}` : null,
      statusLabel ? `Status: ${statusLabel}` : null,
      priorityLabel ? `Priority: ${priorityLabel}` : null,
      warehouseLocationLabel ? `WH: ${warehouseLocationLabel}` : null,
      costUsdStr ? `Cost USD ${costUsdStr}` : null,
      poSeg,
      eta ? `ETA ${d(eta)}` : null,
    ].filter((x): x is string => Boolean(x));
    rows.push({
      kind: 'Forwarding',
      id: fwd.id,
      ref: fwdRef,
      dateStr: d(eta || createdDay),
      sortKey: sortDate,
      detail: detailParts.join(' · '),
      forwardingDetail: {
        forwarderLabel: forwarderLabel.trim() || '—',
        purchaseOrderId: fwd.purchaseOrderId ?? null,
        purchaseOrderLinkLabel:
          fwd.purchaseOrderId != null ? linkedPoFwd?.ref?.trim() || 'Order' : null,
        portLabel: portLabel || null,
        statusLabel: statusLabel || null,
        priorityLabel: priorityLabel || null,
        warehouseLocationLabel: warehouseLocationLabel || null,
        costUsdStr: costUsdStr || null,
        etaDateStr: eta ? d(eta) : null,
        deliveryCount: fwd.deliveryCount ?? 0,
      },
      href: supplyForwardingsConfig.module.route.detail(fwd.id),
    });
  }
  for (const del of data.deliveries) {
    const date = del.dlvDate || del.deliveryDate || null;
    const sup = del.supplier;
    const supLabel = sup ? (sup.code ? `${sup.name ?? ''} (${sup.code})` : (sup.name ?? '')) : '';
    const hasIssues = deliveryHasLineIssuesForCaseView(data.itemsActivity, del.id);
    const vesselId = data.spareCase.vessel?.id;
    const newRequisitionParams = new URLSearchParams();
    newRequisitionParams.set('noCase', '1');
    if (vesselId != null) newRequisitionParams.set('vesselId', String(vesselId));
    if (hasIssues) newRequisitionParams.set('importFromDelivery', String(del.id));
    const newRequisitionOutsideCaseHref = hasIssues
      ? `${requisitionsModuleConfig.module.route.new}?${newRequisitionParams.toString()}`
      : null;
    const dlvRef = del.ref || `DLV-${del.id}`;
    const linkedPoDlv = del.purchaseOrderId != null ? poById.get(del.purchaseOrderId) : undefined;
    const poDetailSegment =
      del.purchaseOrderId != null
        ? linkedPoDlv?.ref?.trim()
          ? `Order ${linkedPoDlv.ref}`
          : 'Linked order'
        : null;
    const dlvIdentity = [`Supplier: ${supLabel}`, dlvRef, poDetailSegment].filter((x): x is string => Boolean(x));
    rows.push({
      kind: 'Delivery',
      id: del.id,
      ref: dlvRef,
      dateStr: d(date),
      sortKey: date || '',
      detail: dlvIdentity.join(' · '),
      deliveryDetail: {
        supplierLabel: supLabel.trim() || '—',
        purchaseOrderId: del.purchaseOrderId ?? null,
        purchaseOrderLinkLabel:
          del.purchaseOrderId != null ? linkedPoDlv?.ref?.trim() || 'Order' : null,
        hasLineIssues: hasIssues,
      },
      href: supplyDeliveriesConfig.module.route.detail(del.id),
      deliveryPurchaseOrderId: del.purchaseOrderId,
      newRequisitionOutsideCaseHref,
    });
  }
  for (const inv of data.invoices) {
    const date = inv.invDate || null;
    const sup = inv.supplier;
    const supLabel = sup ? (sup.code ? `${sup.name ?? ''} (${sup.code})` : (sup.name ?? '')) : '';
    const cur = inv.currency;
    const curLabel = cur ? (cur.symbol ? `${cur.code} (${cur.symbol})` : cur.code) : null;
    const invRef = inv.ref || `INV-${inv.id}`;
    const invUpd = inv.updatedAt ? d(inv.updatedAt) : null;
    const linkedPoInv = inv.purchaseOrderId != null ? poById.get(inv.purchaseOrderId) : undefined;
    const linkedDlvInv = inv.deliveryId != null ? dlvById.get(inv.deliveryId) : undefined;
    const invPoSegment =
      inv.purchaseOrderId != null
        ? linkedPoInv?.ref?.trim()
          ? `Order ${linkedPoInv.ref}`
          : 'Linked order'
        : null;
    const invDlvSegment =
      inv.deliveryId != null
        ? linkedDlvInv?.ref?.trim()
          ? `Delivery ${linkedDlvInv.ref}`
          : 'Linked delivery'
        : null;
    const detailParts = [
      `Supplier: ${supLabel}`,
      invRef,
      invPoSegment,
      invDlvSegment,
      invUpd ? `Updated ${invUpd}` : null,
      curLabel ?? null,
      inv.exchangeRate != null ? `Rate ${inv.exchangeRate}` : null,
      inv.totalLocal != null ? `Total ${inv.totalLocal}${cur?.code ? ` ${cur.code}` : ''}` : null,
      inv.totalUsd != null ? `Total USD ${inv.totalUsd}` : null,
    ].filter((x): x is string => Boolean(x));
    rows.push({
      kind: 'Invoice',
      id: inv.id,
      ref: invRef,
      dateStr: d(date),
      sortKey: date || '',
      detail: detailParts.join(' · '),
      invoiceDetail: {
        supplierLabel: supLabel.trim() || '—',
        purchaseOrderId: inv.purchaseOrderId ?? null,
        deliveryId: inv.deliveryId ?? null,
        purchaseOrderLinkLabel:
          inv.purchaseOrderId != null ? linkedPoInv?.ref?.trim() || 'Order' : null,
        deliveryLinkLabel: inv.deliveryId != null ? linkedDlvInv?.ref?.trim() || 'Delivery' : null,
        currencyLabel: curLabel,
        currencyCode: cur?.code ?? null,
        currencySymbol: cur?.symbol ?? null,
        rate: inv.exchangeRate ?? null,
        totalLocal: inv.totalLocal ?? null,
        totalUsd: inv.totalUsd ?? null,
        updatedDateStr: invUpd,
      },
      href: supplyInvoicesConfig.module.route.detail(inv.id),
    });
  }
  return rows;
};

const STAGE_ORDER: string[] = [
  'Requisition',
  'Supplier enquiry',
  'Quotation',
  'Evaluation',
  'Purchase order',
  'Forwarding',
  'Delivery',
  'Invoice',
];

/** Shown as the native tooltip on the case activity section info control. */
const CASE_ACTIVITY_INFO_TOOLTIP =
  "Requisitions, supplier enquiries, quotations, and the rest of the pipeline. Document references shown in Details and links come from each record's reference fields when set. Choose Table for one list or Stages to group by pipeline step. Use Action, copy, and open in each row as needed.";

const sortActivityRowsNewestFirst = (rows: TableRow[]): TableRow[] => {
  const out = [...rows];
  out.sort((a, b) => {
    const stageDelta = (kindOrder[a.kind] ?? 99) - (kindOrder[b.kind] ?? 99);
    if (stageDelta !== 0) return stageDelta;
    const ca = a.sortKey;
    const cb = b.sortKey;
    if (ca && cb && ca !== cb) {
      return cb.localeCompare(ca);
    }
    if (ca && !cb) return -1;
    if (!ca && cb) return 1;
    return a.id - b.id;
  });
  return out;
};

const groupRowsByStage = (rows: TableRow[]) => {
  const map = new Map<string, TableRow[]>();
  for (const k of STAGE_ORDER) map.set(k, []);
  for (const r of rows) {
    const list = map.get(r.kind);
    if (list) list.push(r);
  }
  return STAGE_ORDER.map((k) => ({ kind: k, rows: map.get(k) ?? [] })).filter((g) => g.rows.length > 0);
};

const supHead = (s: { name: string | null; code: string | null }) => {
  const n = (s.name ?? '').trim() || '';
  return s.code ? `${n} (${s.code})` : n;
};

/** Same 2dp formatting as quotation-evaluation `Detail` line table */
const fmt2 = (n: string | number | null | undefined) => {
  if (n == null || n === '') return '';
  const v = typeof n === 'string' ? Number(n) : n;
  if (!Number.isFinite(v)) return '';
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
};

/** Parse backend decimal strings for summing line / USD amounts in activity footers. */
const parseAmount = (s: string | null | undefined): number | null => {
  if (s == null || s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const orderLineMatchFieldLabel: Record<'qty' | 'unitPrice' | 'lineTotal', string> = {
  qty: 'Quantity',
  unitPrice: 'Unit price',
  lineTotal: 'Line total',
};

/** Renders the narrow “vs PO” cell: invoice line vs linked purchase order line. */
function InvoiceOrderLineMatchCell({
  m,
  colors,
}: {
  m: NonNullable<CaseViewPayload['itemsActivity']>['rows'][0]['invoices'][0]['orderLineMatch'];
  colors: { text: string; textSecondary: string };
}) {
  switch (m.code) {
    case 'no_po':
      return (
        <span className={cn('text-[0.7rem] tabular-nums', colors.textSecondary)} title="No purchase order linked to this invoice">
          —
        </span>
      );
    case 'po_not_in_case':
      return (
        <span
          className="text-[0.7rem] font-semibold text-amber-800 dark:text-amber-200"
          title="Linked order is not loaded for this case; cannot compare"
        >
          ?
        </span>
      );
    case 'n_a':
      return (
        <span
          className={cn('text-[0.7rem]', colors.textSecondary)}
          title="No line on the invoice and no matching line on the order for this item"
        >
          —
        </span>
      );
    case 'ok':
      return (
        <span
          className="text-sm font-semibold text-emerald-700 dark:text-emerald-300"
          title="Matches the linked purchase order line (quantity, unit price, and line total)"
        >
          ✓
        </span>
      );
    case 'mismatch':
      return (
        <span
          className="text-sm font-bold text-amber-800 dark:text-amber-200"
          title={`Differs from the order: ${m.fields.map((f) => orderLineMatchFieldLabel[f]).join(', ')}`}
        >
          !
        </span>
      );
    case 'extra_on_invoice':
      return (
        <span
          className="text-[0.65rem] font-semibold leading-tight text-rose-800 dark:text-rose-200"
          title="This item appears on the invoice but not on the linked purchase order"
        >
          Inv only
        </span>
      );
    case 'missing_on_invoice':
      return (
        <span
          className="text-[0.65rem] font-semibold leading-tight text-amber-950 dark:text-amber-100"
          title="This item is on the linked purchase order but missing on the invoice"
        >
          PO only
        </span>
      );
    default:
      return <span className={colors.textSecondary}>—</span>;
  }
}

/** Renders the “vs RFQ” cell: quotation line vs linked supplier enquiry (RFQ) line — quantities only. */
function QuotationRfqLineMatchCell({
  m,
  colors,
}: {
  m: NonNullable<CaseViewPayload['itemsActivity']>['rows'][0]['quotations'][0]['rfqLineMatch'];
  colors: { text: string; textSecondary: string };
}) {
  switch (m.code) {
    case 'no_rfq':
      return (
        <span className={cn('text-[0.7rem] tabular-nums', colors.textSecondary)} title="No supplier enquiry (RFQ) linked to this quotation">
          —
        </span>
      );
    case 'rfq_not_in_case':
      return (
        <span
          className="text-[0.7rem] font-semibold text-amber-800 dark:text-amber-200"
          title="Linked RFQ is not in this case; cannot compare"
        >
          ?
        </span>
      );
    case 'n_a':
      return (
        <span
          className={cn('text-[0.7rem]', colors.textSecondary)}
          title="No line on the quote and no matching line on the RFQ for this item"
        >
          —
        </span>
      );
    case 'ok':
      return (
        <span
          className="text-sm font-semibold text-emerald-700 dark:text-emerald-300"
          title="Quoted quantity matches the linked RFQ line for this item"
        >
          ✓
        </span>
      );
    case 'mismatch':
      return (
        <span
          className="text-sm font-bold text-amber-800 dark:text-amber-200"
          title={
            m.fields.includes('qty')
              ? 'Quoted quantity differs from the quantity on the linked RFQ for this item'
              : 'Differs from the RFQ'
          }
        >
          !
        </span>
      );
    case 'extra_on_quotation':
      return (
        <span
          className="text-[0.65rem] font-semibold leading-tight text-amber-900 dark:text-amber-200"
          title="This item is quoted but does not appear on the linked supplier enquiry (RFQ)"
        >
          Quote only
        </span>
      );
    case 'missing_on_quotation':
      return (
        <span
          className="text-[0.65rem] font-semibold leading-tight text-amber-950 dark:text-amber-100"
          title="This item is on the linked RFQ but missing from the quotation"
        >
          RFQ only
        </span>
      );
    default:
      return <span className={colors.textSecondary}>—</span>;
  }
}

/** Requisition line rows: one column per enquiry, quotation (evaluation-style), PO, delivery, invoice. */
const CaseItemsActivityTable: React.FC<{
  payload: NonNullable<CaseViewPayload['itemsActivity']>;
}> = ({ payload }) => {
  const { colors } = useTheme();
  const {
    enquiryColumns,
    quotationColumns,
    purchaseOrderColumns,
    deliveryColumns,
    invoiceColumns,
    rows,
  } = payload;

  const thBase = cn('px-1.5 py-2 text-[0.65rem] font-semibold uppercase leading-tight', colors.textSecondary);
  const thUnit = cn(thBase, 'w-8 min-w-[2rem] max-w-[2.75rem] px-0.5 py-2 text-[0.6rem]');
  const thGroup = (borderAccent: string) => cn('border-l-2', borderAccent, colors.border, 'first:border-l-0');

  const totalReqQty = React.useMemo(
    () => rows.reduce((s, r) => s + (typeof r.reqQty === 'number' && r.reqQty > 0 ? r.reqQty : 0), 0),
    [rows],
  );

  const quotationTotals = React.useMemo(
    () =>
      quotationColumns.map((_, i) => {
        let sumQty = 0;
        let hasQty = false;
        let sumLine = 0;
        let hasLine = false;
        let sumUsd = 0;
        let hasUsd = false;
        for (const r of rows) {
          const c = r.quotations[i];
          if (!c?.hasLine) continue;
          if (c.qty != null) {
            sumQty += c.qty;
            hasQty = true;
          }
          const lt = parseAmount(c.lineTotal);
          if (lt != null) {
            sumLine += lt;
            hasLine = true;
          }
          const u = parseAmount(c.lineTotalUsd);
          if (u != null) {
            sumUsd += u;
            hasUsd = true;
          }
        }
        return {
          sumQty: hasQty ? sumQty : null,
          sumLine: hasLine ? sumLine : null,
          sumUsd: hasUsd ? sumUsd : null,
        };
      }),
    [rows, quotationColumns],
  );

  const purchaseOrderTotals = React.useMemo(
    () =>
      purchaseOrderColumns.map((_, i) => {
        let sumQty = 0;
        let hasQty = false;
        let sumLine = 0;
        let hasLine = false;
        let sumUsd = 0;
        let hasUsd = false;
        for (const r of rows) {
          const c = r.purchaseOrders[i];
          if (!c?.hasLine) continue;
          if (c.qty != null) {
            sumQty += c.qty;
            hasQty = true;
          }
          const lt = parseAmount(c.lineTotal);
          if (lt != null) {
            sumLine += lt;
            hasLine = true;
          }
          const u = parseAmount(c.lineTotalUsd);
          if (u != null) {
            sumUsd += u;
            hasUsd = true;
          }
        }
        return {
          sumQty: hasQty ? sumQty : null,
          sumLine: hasLine ? sumLine : null,
          sumUsd: hasUsd ? sumUsd : null,
        };
      }),
    [rows, purchaseOrderColumns],
  );

  const invoiceTotals = React.useMemo(
    () =>
      invoiceColumns.map((_, i) => {
        let sumQty = 0;
        let hasQty = false;
        let sumLine = 0;
        let hasLine = false;
        let sumUsd = 0;
        let hasUsd = false;
        for (const r of rows) {
          const c = r.invoices[i];
          if (!c?.hasLine) continue;
          if (c.qty != null) {
            sumQty += c.qty;
            hasQty = true;
          }
          const lt = parseAmount(c.lineTotal);
          if (lt != null) {
            sumLine += lt;
            hasLine = true;
          }
          const u = parseAmount(c.lineTotalUsd);
          if (u != null) {
            sumUsd += u;
            hasUsd = true;
          }
        }
        return {
          sumQty: hasQty ? sumQty : null,
          sumLine: hasLine ? sumLine : null,
          sumUsd: hasUsd ? sumUsd : null,
        };
      }),
    [rows, invoiceColumns],
  );

  if (rows.length === 0) {
    return (
      <div className={cn('px-4 py-10 text-center text-sm', colors.textSecondary, 'md:px-5')}>
        No line-level items for this case yet. Add requisition lines (or other document lines) to see activity here.
      </div>
    );
  }

  const pipelineColCount =
    enquiryColumns.length +
    quotationColumns.length * 5 +
    purchaseOrderColumns.length * 4 +
    deliveryColumns.length +
    invoiceColumns.length * 5;
  const hasPipelineCols = pipelineColCount > 0;

  const tfootBase = cn('border-t-2', colors.border, 'bg-slate-100/90 font-semibold dark:bg-slate-800/50', colors.text);

  return (
    <div
      className={cn(
        'max-h-[min(70vh,52rem)] overflow-auto rounded-b-lg',
        colors.border,
      )}
    >
      <table className="min-w-max w-full text-sm">
        <thead>
          {!hasPipelineCols ? (
            <tr className={cn('sticky top-0 z-30 border-b shadow-sm', colors.border, colors.surface)}>
              <th
                className={cn(
                  thBase,
                  'sticky left-0 top-0 z-40 min-w-[12rem] max-w-[20rem] bg-white px-2 py-2.5 pl-3 text-left dark:bg-slate-900',
                  'md:pl-4',
                )}
              >
                Item
              </th>
              <th className={cn(thUnit, 'bg-white dark:bg-slate-900')}>Unit</th>
              <th
                className={cn(
                  thBase,
                  'w-10 min-w-[2.25rem] bg-white text-center align-bottom dark:bg-slate-900',
                )}
                title="Store item history (requisitions, RFQ, quotes, orders, deliveries, invoices, ports, prices)"
              >
                Hist.
              </th>
              <th className={cn(thBase, 'w-20 min-w-[5rem] bg-white text-center tabular-nums dark:bg-slate-900')}>
                Req qty
              </th>
            </tr>
          ) : null}
          {hasPipelineCols ? (
            <>
          <tr className={cn('sticky top-0 z-30 border-b shadow-sm', colors.border)}>
            <th rowSpan={2} className={cn(thBase, 'sticky left-0 top-0 z-40 min-w-[12rem] max-w-[20rem] bg-white px-2 py-2 pl-3 align-bottom dark:bg-slate-900', 'md:pl-4')}>
              Item
            </th>
            <th rowSpan={2} className={cn(thUnit, 'align-bottom bg-white dark:bg-slate-900')}>
              Unit
            </th>
            <th
              rowSpan={2}
              className={cn(
                thBase,
                'w-10 min-w-[2.25rem] bg-white text-center align-bottom dark:bg-slate-900',
              )}
              title="Store item history (requisitions, RFQ, quotes, orders, deliveries, invoices, ports, prices)"
            >
              Hist.
            </th>
            <th
              rowSpan={2}
              className={cn(thBase, 'w-20 min-w-[5rem] bg-white text-center align-bottom tabular-nums dark:bg-slate-900')}
            >
              Req qty
            </th>
            {enquiryColumns.length > 0 ? (
              <th
                colSpan={enquiryColumns.length}
                className={cn(thBase, thGroup('border-violet-400/80'), 'bg-violet-50/50 text-center dark:bg-violet-950/25')}
              >
                Enquiries (per supplier)
              </th>
            ) : null}
            {quotationColumns.map((c, i) => (
              <th
                key={`quo-grp-${c.id}`}
                colSpan={5}
                className={cn(
                  thBase,
                  'min-w-[13rem] border-l text-left align-top',
                  'border-amber-300/70 dark:border-amber-800/70',
                  'bg-amber-50/50 dark:bg-amber-950/20',
                  i === 0 ? 'border-l-2 border-amber-400/80' : '',
                )}
                title={`Quote ${c.ref?.trim() || '—'} — ${supHead(c.supplier)}${c.currencyCode ? ` · ${c.currencyCode}` : ''}`}
              >
                <div className="px-1.5 py-2">
                  <div className={cn('truncate text-[0.75rem] font-semibold normal-case', colors.text)}>{supHead(c.supplier)}</div>
                  <div className={cn('mt-0.5 text-[0.65rem] font-normal normal-case', colors.textSecondary)}>
                    {c.ref?.trim() || 'Quotation'}
                  </div>
                  {c.currencyCode ? (
                    <div className="mt-0.5 text-[0.6rem] tabular-nums text-amber-900/90 dark:text-amber-200/90">
                      {c.currencySymbol ? `${c.currencyCode} ${c.currencySymbol}` : c.currencyCode}
                    </div>
                  ) : null}
                  <div className="mt-1.5 border-t border-amber-200/55 pt-1.5 dark:border-amber-800/45">
                    <p className={cn('text-[0.55rem] font-semibold uppercase tracking-wide', colors.textSecondary)}>
                      Supplier portal
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-x-1 gap-y-0.5">
                      <span className={cn('text-[0.55rem] font-medium', colors.textSecondary)}>Status</span>
                      <span
                        className={cn(
                          'rounded px-1.5 py-0.5 text-[0.58rem] font-semibold',
                          c.portalOfferStatus === 'submitted'
                            ? 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/45 dark:text-emerald-100'
                            : 'bg-amber-100 text-amber-950 dark:bg-amber-900/35 dark:text-amber-100',
                        )}
                      >
                        {portalOfferStatusCompactLabel(c.portalOfferStatus)}
                      </span>
                      <button
                        type="button"
                        className={cn(
                          'inline-flex shrink-0 rounded-full p-0.5 outline-none transition-colors',
                          'text-amber-800/70 hover:bg-amber-200/50 hover:text-amber-950',
                          'dark:text-amber-200/75 dark:hover:bg-amber-900/50 dark:hover:text-amber-100',
                          'focus-visible:ring-2 focus-visible:ring-amber-400/80 focus-visible:ring-offset-1',
                        )}
                        title={quotationPortalStatusTooltip(
                          c.portalOfferStatus,
                          c.rfqPortalLastOpenedAt,
                          c.rfqPortalLastSavedAt,
                          c.rfqPublicSubmittedAt,
                        )}
                        aria-label="Supplier portal status: what this means and timestamps"
                      >
                        <InfoIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <dl className={cn('mt-1.5 space-y-1 text-[0.58rem] leading-snug', colors.text)}>
                      <div>
                        <dt className={cn('font-medium', colors.textSecondary)}>Last login</dt>
                        <dd className="tabular-nums">
                          {c.rfqPortalLastOpenedAt ? formatDateTimeGlobal(c.rfqPortalLastOpenedAt) : '—'}
                        </dd>
                      </div>
                      <div>
                        <dt className={cn('font-medium', colors.textSecondary)}>Last saved</dt>
                        <dd className="tabular-nums">
                          {c.rfqPortalLastSavedAt ? formatDateTimeGlobal(c.rfqPortalLastSavedAt) : '—'}
                        </dd>
                      </div>
                      <div>
                        <dt className={cn('font-medium', colors.textSecondary)}>Last submitted</dt>
                        <dd className="tabular-nums">
                          {c.rfqPublicSubmittedAt ? formatDateTimeGlobal(c.rfqPublicSubmittedAt) : '—'}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>
              </th>
            ))}
            {purchaseOrderColumns.map((c, i) => (
              <th
                key={`po-grp-${c.id}`}
                colSpan={4}
                className={cn(
                  thBase,
                  'min-w-0 border-l text-left align-top',
                  'border-emerald-300/70 dark:border-emerald-800/70',
                  'bg-emerald-50/50 dark:bg-emerald-950/20',
                  i === 0 ? 'border-l-2 border-emerald-400/80' : '',
                )}
                title={`PO ${c.ref?.trim() || '—'} — ${supHead(c.supplier)}${c.currencyCode ? ` · ${c.currencyCode}` : ''}`}
              >
                <div className="px-1.5 py-2">
                  <div className={cn('truncate text-[0.75rem] font-semibold normal-case', colors.text)}>{supHead(c.supplier)}</div>
                  <div className={cn('mt-0.5 text-[0.65rem] font-normal normal-case', colors.textSecondary)}>
                    {c.ref?.trim() || 'Order'}
                  </div>
                  {c.currencyCode ? (
                    <div className="mt-0.5 text-[0.6rem] tabular-nums text-emerald-900/90 dark:text-emerald-200/90">
                      {c.currencySymbol ? `${c.currencyCode} ${c.currencySymbol}` : c.currencyCode}
                    </div>
                  ) : null}
                </div>
              </th>
            ))}
            {deliveryColumns.length > 0 ? (
              <th
                colSpan={deliveryColumns.length}
                className={cn(thBase, thGroup('border-sky-400/80'), 'bg-sky-50/50 text-center dark:bg-sky-950/20')}
              >
                Deliveries
              </th>
            ) : null}
            {invoiceColumns.map((c, i) => (
              <th
                key={`inv-grp-${c.id}`}
                colSpan={5}
                className={cn(
                  thBase,
                  'min-w-0 border-l text-left align-top',
                  'border-rose-300/70 dark:border-rose-800/70',
                  'bg-rose-50/50 dark:bg-rose-950/20',
                  i === 0 ? 'border-l-2 border-rose-400/80' : '',
                )}
                title={`Inv ${c.ref?.trim() || '—'} — ${supHead(c.supplier)}${c.currencyCode ? ` · ${c.currencyCode}` : ''}`}
              >
                <div className="px-1.5 py-2">
                  <div className={cn('truncate text-[0.75rem] font-semibold normal-case', colors.text)}>{supHead(c.supplier)}</div>
                  <div className={cn('mt-0.5 text-[0.65rem] font-normal normal-case', colors.textSecondary)}>
                    {c.ref?.trim() || 'Invoice'}
                  </div>
                  {c.currencyCode ? (
                    <div className="mt-0.5 text-[0.6rem] tabular-nums text-rose-900/90 dark:text-rose-200/90">
                      {c.currencySymbol ? `${c.currencyCode} ${c.currencySymbol}` : c.currencyCode}
                    </div>
                  ) : null}
                </div>
              </th>
            ))}
          </tr>
          <tr className={cn('sticky top-[4.25rem] z-20 border-b shadow-sm', colors.border)}>
            {enquiryColumns.map((c, i) => (
              <th
                key={`enq-h-${c.id}`}
                className={cn(
                  thBase,
                  thGroup('border-violet-400/80'),
                  'min-w-[5.5rem] max-w-[7rem] font-normal normal-case',
                  i === 0 && enquiryColumns.length > 0 ? 'border-l-2 border-violet-400/80' : '',
                )}
                title={`Enquiry ${c.ref?.trim() || '—'} — ${supHead(c.supplier)}`}
              >
                <span className="block truncate text-[0.6rem] font-medium">{c.ref?.trim() || 'Enquiry'}</span>
                <span className="mt-0.5 block truncate text-[0.58rem] opacity-80">{supHead(c.supplier)}</span>
              </th>
            ))}
            {quotationColumns.map((c, i) => {
              const quoStripe = i % 2 === 0 ? 'bg-black/[0.02] dark:bg-white/[0.03]' : '';
              return (
                <React.Fragment key={`quo-subhead-${c.id}`}>
                  <th
                    className={cn(
                      'px-2 py-1 text-left text-[0.65rem] font-semibold uppercase tracking-wide',
                      colors.textSecondary,
                      'border-l border-amber-300/70 dark:border-amber-800/70',
                      quoStripe,
                      i === 0 ? 'border-l-2 border-amber-400/80' : '',
                    )}
                  >
                    Qty
                  </th>
                  <th
                    className={cn('px-2 py-1 text-right text-[0.65rem] font-semibold uppercase', colors.textSecondary, quoStripe)}
                  >
                    U. price
                  </th>
                  <th
                    className={cn('px-2 py-1 text-right text-[0.65rem] font-semibold uppercase', colors.textSecondary, quoStripe)}
                  >
                    Total
                  </th>
                  <th
                    className={cn('px-2 py-1 text-right text-[0.65rem] font-semibold uppercase', colors.textSecondary, quoStripe)}
                  >
                    Total USD
                  </th>
                  <th
                    className={cn(
                      'px-1.5 py-1 text-center text-[0.6rem] font-semibold uppercase leading-tight',
                      colors.textSecondary,
                      quoStripe,
                    )}
                    title="Compares this quoted line to the linked RFQ (supplier enquiry) line: quantity on both sides"
                  >
                    vs RFQ
                  </th>
                </React.Fragment>
              );
            })}
            {purchaseOrderColumns.map((c, i) => {
              const poStripe = i % 2 === 0 ? 'bg-black/[0.02] dark:bg-white/[0.03]' : '';
              return (
                <React.Fragment key={`po-subhead-${c.id}`}>
                  <th
                    className={cn(
                      'px-2 py-1 text-left text-[0.65rem] font-semibold uppercase tracking-wide',
                      colors.textSecondary,
                      'border-l border-emerald-300/70 dark:border-emerald-800/70',
                      poStripe,
                      i === 0 ? 'border-l-2 border-emerald-400/80' : '',
                    )}
                  >
                    Qty
                  </th>
                  <th
                    className={cn('px-2 py-1 text-right text-[0.65rem] font-semibold uppercase', colors.textSecondary, poStripe)}
                  >
                    U. price
                  </th>
                  <th
                    className={cn('px-2 py-1 text-right text-[0.65rem] font-semibold uppercase', colors.textSecondary, poStripe)}
                  >
                    Total
                  </th>
                  <th
                    className={cn('px-2 py-1 text-right text-[0.65rem] font-semibold uppercase', colors.textSecondary, poStripe)}
                  >
                    Total USD
                  </th>
                </React.Fragment>
              );
            })}
            {deliveryColumns.map((c, i) => {
              const dlvHeadRef = c.ref?.trim() || 'Delivery';
              const linkedPoForTooltip =
                c.purchaseOrderId != null
                  ? purchaseOrderColumns.find((p) => p.id === c.purchaseOrderId)
                  : undefined;
              const poTooltipSeg =
                c.purchaseOrderId != null
                  ? linkedPoForTooltip?.ref?.trim()
                    ? ` · Order ${linkedPoForTooltip.ref}`
                    : ' · Linked order'
                  : '';
              return (
              <th
                key={`dlv-h-${c.id}`}
                className={cn(
                  thBase,
                  'min-w-[5.5rem] max-w-[7rem] align-top font-normal normal-case',
                  i === 0 && deliveryColumns.length > 0 ? 'border-l-2 border-sky-400/80' : '',
                )}
                title={
                  c.lineCount === 0
                    ? `${dlvHeadRef} — no lines`
                    : `${dlvHeadRef}${poTooltipSeg} · ${
                        c.allLinesReceived ? 'All line items marked received' : 'Some line items not yet received'
                      }`
                }
              >
                <span className="block text-center text-[0.6rem] font-medium">{dlvHeadRef}</span>
                {c.lineCount > 0 ? (
                  <span
                    className={cn(
                      'mt-1 block rounded px-1.5 py-0.5 text-center text-[0.55rem] font-semibold uppercase leading-tight',
                      c.allLinesReceived
                        ? 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200'
                        : 'bg-amber-100 text-amber-950 dark:bg-amber-900/40 dark:text-amber-100',
                    )}
                  >
                    {c.allLinesReceived ? 'Complete' : 'Open'}
                  </span>
                ) : (
                  <span className={cn('mt-1 block text-center text-[0.55rem] opacity-70', colors.textSecondary)}>No lines</span>
                )}
              </th>
              );
            })}
            {invoiceColumns.map((c, i) => {
              const invStripe = i % 2 === 0 ? 'bg-black/[0.02] dark:bg-white/[0.03]' : '';
              return (
                <React.Fragment key={`inv-subhead-${c.id}`}>
                  <th
                    className={cn(
                      'px-2 py-1 text-left text-[0.65rem] font-semibold uppercase tracking-wide',
                      colors.textSecondary,
                      'border-l border-rose-300/70 dark:border-rose-800/70',
                      invStripe,
                      i === 0 ? 'border-l-2 border-rose-400/80' : '',
                    )}
                  >
                    Qty
                  </th>
                  <th
                    className={cn('px-2 py-1 text-right text-[0.65rem] font-semibold uppercase', colors.textSecondary, invStripe)}
                  >
                    U. price
                  </th>
                  <th
                    className={cn('px-2 py-1 text-right text-[0.65rem] font-semibold uppercase', colors.textSecondary, invStripe)}
                  >
                    Total
                  </th>
                  <th
                    className={cn('px-2 py-1 text-right text-[0.65rem] font-semibold uppercase', colors.textSecondary, invStripe)}
                  >
                    Total USD
                  </th>
                  <th
                    className={cn(
                      'px-1.5 py-1 text-center text-[0.6rem] font-semibold uppercase leading-tight',
                      colors.textSecondary,
                      invStripe,
                    )}
                    title="Compares this line to the linked purchase order line: qty, unit price, and line total"
                  >
                    vs PO
                  </th>
                </React.Fragment>
              );
            })}
          </tr>
            </>
          ) : null}
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.rowKey}
              className={cn('border-b transition-colors last:border-0', colors.border, 'hover:bg-gray-50/80 dark:hover:bg-white/[0.04]')}
            >
              <td
                className={cn(
                  'sticky left-0 z-10 min-w-[12rem] max-w-[20rem] bg-white px-2 py-2 align-top pl-3 dark:bg-slate-900',
                  'md:pl-4',
                )}
              >
                <div className="text-xs font-medium tabular-nums text-slate-600 dark:text-slate-300">{r.itemVcode || ''}</div>
                <div className="mt-0.5 min-w-0 break-words text-sm font-medium leading-snug">{r.itemDescr || ''}</div>
              </td>
              <td
                className={cn(
                  'w-8 min-w-[2rem] max-w-[2.75rem] shrink-0 align-top py-2 px-0.5 text-center text-[0.65rem] leading-tight',
                  colors.textSecondary,
                )}
                title={r.unit?.descr ? `${r.unit.descr}` : r.unit?.ucode ?? undefined}
              >
                <span className="line-clamp-2 break-all">{r.unit ? r.unit.ucode : ''}</span>
              </td>
              <td className="w-10 min-w-[2.25rem] align-middle py-2 text-center">
                {r.itemId != null ? (
                  <Link
                    to={`/spares/spare-items/${r.itemId}`}
                    className={cn('inline-flex rounded p-1 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30', colors.text)}
                    title="Open spare item"
                  >
                    ↗
                  </Link>
                ) : (
                  <span className={cn('text-xs', colors.textSecondary)}>—</span>
                )}
              </td>
              <td className={cn('w-20 min-w-[5rem] align-top py-2 text-center text-sm tabular-nums', colors.text)}>
                {r.reqQty > 0 ? r.reqQty : ''}
              </td>
              {r.enquiries.map((cell, i) => (
                <td
                  key={`${r.rowKey}-enq-${enquiryColumns[i]?.id}`}
                  className={cn(
                    'min-w-[5.5rem] max-w-[7rem] border-l border-violet-200/50 px-1.5 py-2 text-center text-xs tabular-nums dark:border-violet-900/40',
                    i === 0 && enquiryColumns.length > 0 ? 'border-l-2 border-violet-300/60 dark:border-violet-800/50' : '',
                    colors.text,
                  )}
                >
                  {cell.hasLine ? (
                    <span className="font-medium text-violet-800 dark:text-violet-200" title="RFQ line present">
                      {cell.qty != null ? cell.qty : '✓'}
                    </span>
                  ) : (
                    <span className={colors.textSecondary}>—</span>
                  )}
                </td>
              ))}
              {r.quotations.map((cell, i) => {
                const quoStripe = i % 2 === 0 ? 'bg-black/[0.02] dark:bg-white/[0.03]' : '';
                const qid = quotationColumns[i]?.id;
                return (
                  <React.Fragment key={qid != null ? `${r.rowKey}-q-${qid}` : `${r.rowKey}-q-${i}`}>
                    <td
                      className={cn(
                        'border-l border-amber-300/70 px-2 py-2 align-top dark:border-amber-800/70',
                        quoStripe,
                        i === 0 ? 'border-l-2 border-amber-400/80' : '',
                        colors.text,
                      )}
                    >
                      {cell.hasLine && cell.qty != null ? (
                        <span className="tabular-nums">{cell.qty}</span>
                      ) : (
                        <span className={colors.textSecondary}>—</span>
                      )}
                    </td>
                    <td
                      className={cn(
                        'px-2 py-2 text-right tabular-nums align-top whitespace-nowrap',
                        quoStripe,
                        colors.text,
                      )}
                    >
                      {cell.hasLine && cell.unitPrice != null ? fmt2(cell.unitPrice) : ''}
                    </td>
                    <td
                      className={cn(
                        'px-2 py-2 text-right tabular-nums align-top whitespace-nowrap',
                        quoStripe,
                        colors.text,
                      )}
                    >
                      {cell.hasLine && cell.lineTotal != null ? fmt2(cell.lineTotal) : ''}
                    </td>
                    <td
                      className={cn(
                        'px-2 py-2 text-right text-xs tabular-nums align-top',
                        quoStripe,
                        colors.textSecondary,
                      )}
                      title={
                        cell.hasLine && cell.lineTotal != null && (cell.lineTotalUsd == null || cell.lineTotalUsd === '')
                          ? 'Set the quotation rate (FX) to compute USD for non-USD lines'
                          : undefined
                      }
                    >
                      {cell.hasLine && cell.lineTotalUsd != null ? fmt2(cell.lineTotalUsd) : ''}
                    </td>
                    <td
                      className={cn(
                        'min-w-[3.5rem] max-w-[4.5rem] border-l border-amber-200/60 px-1 py-2 text-center align-middle dark:border-amber-900/40',
                        quoStripe,
                      )}
                    >
                      <div className="flex min-h-[1.25rem] items-center justify-center">
                        {cell.rfqLineMatch != null ? (
                          <QuotationRfqLineMatchCell m={cell.rfqLineMatch} colors={colors} />
                        ) : (
                          <span className={colors.textSecondary}>—</span>
                        )}
                      </div>
                    </td>
                  </React.Fragment>
                );
              })}
              {r.purchaseOrders.map((cell, i) => {
                const poStripe = i % 2 === 0 ? 'bg-black/[0.02] dark:bg-white/[0.03]' : '';
                const pid = purchaseOrderColumns[i]?.id;
                return (
                  <React.Fragment key={pid != null ? `${r.rowKey}-po-${pid}` : `${r.rowKey}-po-${i}`}>
                    <td
                      className={cn(
                        'border-l border-emerald-300/70 px-2 py-2 align-top dark:border-emerald-800/70',
                        poStripe,
                        i === 0 ? 'border-l-2 border-emerald-400/80' : '',
                        colors.text,
                      )}
                    >
                      {cell.hasLine && cell.qty != null ? (
                        <span className="tabular-nums">{cell.qty}</span>
                      ) : (
                        <span className={colors.textSecondary}>—</span>
                      )}
                    </td>
                    <td
                      className={cn('px-2 py-2 text-right tabular-nums align-top whitespace-nowrap', poStripe, colors.text)}
                    >
                      {cell.hasLine && cell.unitPrice != null ? fmt2(cell.unitPrice) : ''}
                    </td>
                    <td
                      className={cn('px-2 py-2 text-right tabular-nums align-top whitespace-nowrap', poStripe, colors.text)}
                    >
                      {cell.hasLine && cell.lineTotal != null ? fmt2(cell.lineTotal) : ''}
                    </td>
                    <td
                      className={cn('px-2 py-2 text-right text-xs tabular-nums align-top', poStripe, colors.textSecondary)}
                      title={
                        cell.hasLine && cell.lineTotal != null && (cell.lineTotalUsd == null || cell.lineTotalUsd === '')
                          ? 'Set the order exchange rate to compute USD for non-USD lines'
                          : undefined
                      }
                    >
                      {cell.hasLine && cell.lineTotalUsd != null ? fmt2(cell.lineTotalUsd) : ''}
                    </td>
                  </React.Fragment>
                );
              })}
              {r.deliveries.map((cell, i) => {
                const poQ = cell.purchaseOrderQty;
                const dlvQ = cell.deliveryQty;
                /** Quantity still not received vs PO, when both sides are known. */
                const shortfall =
                  poQ != null && dlvQ != null ? Math.max(0, poQ - dlvQ) : null;
                const pendingLabel = (() => {
                  if (cell.isDelivered !== false) return null;
                  if (shortfall != null && shortfall > 0) {
                    return { text: `${shortfall} Pending`, title: `${shortfall} unit(s) short of the purchase order line (not yet received)` };
                  }
                  if (poQ != null && dlvQ == null) {
                    return { text: `${poQ} Pending`, title: 'Full purchase order line quantity not yet received on this delivery' };
                  }
                  if (dlvQ != null && poQ == null) {
                    return { text: `${dlvQ} Pending`, title: 'Delivery quantity not yet marked received' };
                  }
                  if (shortfall === 0) {
                    return {
                      text: 'Pending',
                      title: 'Delivery quantity matches the order; not yet marked received on this line',
                    };
                  }
                  return { text: 'Pending', title: 'Not yet marked received on this line' };
                })();
                return (
                <td
                  key={`${r.rowKey}-d-${deliveryColumns[i]?.id}`}
                  className={cn(
                    'min-w-[4.5rem] border-l border-sky-200/50 px-1.5 py-2 text-center text-sm dark:border-sky-900/40',
                    i === 0 && deliveryColumns.length > 0 ? 'border-l-2 border-sky-300/60 dark:border-sky-800/50' : '',
                    colors.text,
                  )}
                >
                  {cell.hasLine ? (
                    <div className="flex flex-col items-center gap-1">
                      <span className="tabular-nums">
                        {dlvQ != null ? dlvQ : ''}
                        {poQ != null && dlvQ != null && poQ !== dlvQ ? (
                          <span className={cn('text-[0.65rem] opacity-80', colors.textSecondary)}> / {poQ}</span>
                        ) : null}
                      </span>
                      {cell.isDelivered === true ? (
                        <span
                          className="whitespace-nowrap rounded px-1.5 py-0.5 text-[0.6rem] font-semibold text-emerald-800 dark:text-emerald-200"
                          title="This line is marked received"
                        >
                          Received
                        </span>
                      ) : cell.isDelivered === false && pendingLabel ? (
                        <span
                          className="whitespace-nowrap rounded px-1.5 py-0.5 text-[0.6rem] font-semibold text-amber-900 dark:text-amber-100"
                          title={pendingLabel.title}
                        >
                          {pendingLabel.text}
                        </span>
                      ) : null}
                    </div>
                  ) : (
                    ''
                  )}
                </td>
              );
              })}
              {r.invoices.map((cell, i) => {
                const invStripe = i % 2 === 0 ? 'bg-black/[0.02] dark:bg-white/[0.03]' : '';
                const iid = invoiceColumns[i]?.id;
                return (
                  <React.Fragment key={iid != null ? `${r.rowKey}-i-${iid}` : `${r.rowKey}-i-${i}`}>
                    <td
                      className={cn(
                        'border-l border-rose-300/70 px-2 py-2 align-top dark:border-rose-800/70',
                        invStripe,
                        i === 0 ? 'border-l-2 border-rose-400/80' : '',
                        colors.text,
                      )}
                    >
                      {cell.hasLine && cell.qty != null ? (
                        <span className="tabular-nums">{cell.qty}</span>
                      ) : (
                        <span className={colors.textSecondary}>—</span>
                      )}
                    </td>
                    <td className={cn('px-2 py-2 text-right tabular-nums align-top whitespace-nowrap', invStripe, colors.text)}>
                      {cell.hasLine && cell.unitPrice != null ? fmt2(cell.unitPrice) : ''}
                    </td>
                    <td className={cn('px-2 py-2 text-right tabular-nums align-top whitespace-nowrap', invStripe, colors.text)}>
                      {cell.hasLine && cell.lineTotal != null ? fmt2(cell.lineTotal) : ''}
                    </td>
                    <td
                      className={cn('px-2 py-2 text-right text-xs tabular-nums align-top', invStripe, colors.textSecondary)}
                      title={
                        cell.hasLine && cell.lineTotal != null && (cell.lineTotalUsd == null || cell.lineTotalUsd === '')
                          ? 'Set the invoice exchange rate to compute USD for non-USD lines'
                          : undefined
                      }
                    >
                      {cell.hasLine && cell.lineTotalUsd != null ? fmt2(cell.lineTotalUsd) : ''}
                    </td>
                    <td
                      className={cn(
                        'min-w-[3.5rem] max-w-[4.5rem] border-l border-rose-200/60 px-1 py-2 text-center align-middle dark:border-rose-900/40',
                        invStripe,
                      )}
                    >
                      <div className="flex min-h-[1.25rem] items-center justify-center">
                        {cell.orderLineMatch != null ? (
                          <InvoiceOrderLineMatchCell m={cell.orderLineMatch} colors={colors} />
                        ) : (
                          <span className={colors.textSecondary}>—</span>
                        )}
                      </div>
                    </td>
                  </React.Fragment>
                );
              })}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className={tfootBase}>
            <td
              className={cn(
                'sticky left-0 z-10 min-w-[12rem] max-w-[20rem] px-2 py-2.5 pl-3 text-left text-sm md:pl-4',
                'bg-slate-100/90 dark:bg-slate-800/50',
              )}
            >
              Total
            </td>
            <td className="w-8 min-w-[2rem] max-w-[2.75rem] px-0.5 py-2 text-center text-[0.65rem] text-slate-500 dark:text-slate-400">
              —
            </td>
            <td className="w-10 min-w-[2.25rem] py-2 text-center text-slate-500 dark:text-slate-400">—</td>
            <td className="w-20 min-w-[5rem] py-2 text-center text-sm tabular-nums">
              {totalReqQty > 0 ? totalReqQty : ''}
            </td>
            {enquiryColumns.map((e) => (
              <td
                key={`ft-enq-${e.id}`}
                className="min-w-[5.5rem] max-w-[7rem] border-l border-violet-200/50 px-1.5 py-2 text-center text-xs dark:border-violet-900/40"
              >
                —
              </td>
            ))}
            {quotationTotals.map((t, i) => {
              const quoStripe = i % 2 === 0 ? 'bg-slate-100/90 dark:bg-slate-800/50' : 'bg-slate-100/70 dark:bg-slate-800/40';
              return (
                <React.Fragment key={`ft-q-${quotationColumns[i]?.id ?? i}`}>
                  <td
                    className={cn(
                      'border-l border-amber-300/70 px-2 py-2 text-left tabular-nums dark:border-amber-800/70',
                      quoStripe,
                      i === 0 ? 'border-l-2 border-amber-400/80' : '',
                    )}
                  >
                    {t.sumQty != null ? t.sumQty : ''}
                  </td>
                  <td className={cn('px-2 py-2 text-right text-xs text-slate-500 dark:text-slate-400', quoStripe)}>—</td>
                  <td className={cn('px-2 py-2 text-right tabular-nums', quoStripe)}>
                    {t.sumLine != null ? fmt2(t.sumLine) : ''}
                  </td>
                  <td className={cn('px-2 py-2 text-right text-xs tabular-nums text-slate-700 dark:text-slate-300', quoStripe)}>
                    {t.sumUsd != null ? fmt2(t.sumUsd) : ''}
                  </td>
                  <td
                    className={cn('min-w-[3.5rem] border-l border-amber-200/60 px-1 py-2 text-center text-xs text-slate-500 dark:border-amber-900/50', quoStripe)}
                  >
                    —
                  </td>
                </React.Fragment>
              );
            })}
            {purchaseOrderTotals.map((t, i) => {
              const poStripe = i % 2 === 0 ? 'bg-slate-100/90 dark:bg-slate-800/50' : 'bg-slate-100/70 dark:bg-slate-800/40';
              return (
                <React.Fragment key={`ft-po-${purchaseOrderColumns[i]?.id ?? i}`}>
                  <td
                    className={cn(
                      'border-l border-emerald-300/70 px-2 py-2 text-left tabular-nums dark:border-emerald-800/70',
                      poStripe,
                      i === 0 ? 'border-l-2 border-emerald-400/80' : '',
                    )}
                  >
                    {t.sumQty != null ? t.sumQty : ''}
                  </td>
                  <td className={cn('px-2 py-2 text-right text-xs text-slate-500 dark:text-slate-400', poStripe)}>—</td>
                  <td className={cn('px-2 py-2 text-right tabular-nums', poStripe)}>
                    {t.sumLine != null ? fmt2(t.sumLine) : ''}
                  </td>
                  <td className={cn('px-2 py-2 text-right text-xs tabular-nums text-slate-700 dark:text-slate-300', poStripe)}>
                    {t.sumUsd != null ? fmt2(t.sumUsd) : ''}
                  </td>
                </React.Fragment>
              );
            })}
            {deliveryColumns.map((d, i) => (
              <td
                key={`ft-dlv-${d.id}`}
                className={cn(
                  'min-w-[4.5rem] border-l border-sky-200/50 px-1.5 py-2 text-center text-sm dark:border-sky-900/40',
                  i === 0 && deliveryColumns.length > 0 ? 'border-l-2 border-sky-300/60 dark:border-sky-800/50' : '',
                )}
              >
                —
              </td>
            ))}
            {invoiceTotals.map((t, i) => {
              const invStripe = i % 2 === 0 ? 'bg-slate-100/90 dark:bg-slate-800/50' : 'bg-slate-100/70 dark:bg-slate-800/40';
              return (
                <React.Fragment key={`ft-i-${invoiceColumns[i]?.id ?? i}`}>
                  <td
                    className={cn(
                      'border-l border-rose-300/70 px-2 py-2 text-left tabular-nums dark:border-rose-800/70',
                      invStripe,
                      i === 0 ? 'border-l-2 border-rose-400/80' : '',
                    )}
                  >
                    {t.sumQty != null ? t.sumQty : ''}
                  </td>
                  <td className={cn('px-2 py-2 text-right text-xs text-slate-500 dark:text-slate-400', invStripe)}>—</td>
                  <td className={cn('px-2 py-2 text-right tabular-nums', invStripe)}>
                    {t.sumLine != null ? fmt2(t.sumLine) : ''}
                  </td>
                  <td className={cn('px-2 py-2 text-right text-xs tabular-nums text-slate-700 dark:text-slate-300', invStripe)}>
                    {t.sumUsd != null ? fmt2(t.sumUsd) : ''}
                  </td>
                  <td
                    className={cn('min-w-[3.5rem] border-l border-rose-200/60 px-1 py-2 text-center text-xs text-slate-500 dark:border-rose-900/50', invStripe)}
                  >
                    —
                  </td>
                </React.Fragment>
              );
            })}
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

const CaseView: React.FC = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { showError, showSuccess } = useNotification();
  const { colors } = useTheme();
  const id = idParam && /^\d+$/.test(idParam) ? Number(idParam) : NaN;
  const caseViewKey = Number.isFinite(id) ? `/spare-cases/${id}/case-view` : null;
  const { mutate: globalMutate } = useSWRConfig();
  const { data, error, isLoading, mutate: revalidateCaseView } = useSWR<CaseViewPayload>(caseViewKey, fetcher, {
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    revalidateOnMount: true,
  });

  /** Case + items activity both come from `caseViewKey`; re-run after returning from pipeline edits. */
  React.useEffect(() => {
    if (!caseViewKey) return;
    void revalidateCaseView();
  }, [caseViewKey, revalidateCaseView]);

  React.useEffect(() => {
    if (!caseViewKey) return;
    const onPageShow = (ev: PageTransitionEvent) => {
      if (ev.persisted) void revalidateCaseView();
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [caseViewKey, revalidateCaseView]);

  const refreshSpareCaseCaches = React.useCallback(async () => {
    await revalidateCaseView();
    if (!Number.isFinite(id)) return;
    const base = `/spare-cases/${id}`;
    await globalMutate(
      (key) =>
        typeof key === 'string' &&
        (key === `${base}/case-view` ||
          key === base ||
          key.startsWith(`${base}/`) ||
          key.startsWith('/spare-cases?')),
    );
  }, [id, revalidateCaseView, globalMutate]);
  const item = data?.spareCase;
  const formatDate = (s?: string | null) => (s ? formatDateGlobal(s) : '');
  const listRoute = moduleConfig.module.route.list;
  const returnPath = React.useMemo(() => getLocationReturnPath(location.state), [location.state]);
  const goBack = useNavigateBack(listRoute, returnPath);

  const [importReqOpen, setImportReqOpen] = React.useState(false);
  const [importSelectedId, setImportSelectedId] = React.useState('');
  const [importBusy, setImportBusy] = React.useState(false);
  const [combineReqOpen, setCombineReqOpen] = React.useState(false);
  const [combineReqBusy, setCombineReqBusy] = React.useState(false);
  const [combineReqIds, setCombineReqIds] = React.useState<Array<string | number>>([]);

  const vesselIdForImport =
    data?.spareCase?.vessel?.id != null ? String(data.spareCase.vessel.id) : '';
  const importCandidatesUrl = React.useMemo(() => {
    if (!importReqOpen || !vesselIdForImport) return null;
    const filters: Record<string, string[]> = {
      noCase: ['true'],
      vesselId: [vesselIdForImport],
      hasLines: ['true'],
      saveStatus: ['SAVED'],
    };
    return `/spare-requisitions?page=1&limit=200&filters=${encodeURIComponent(JSON.stringify({ filters }))}`;
  }, [importReqOpen, vesselIdForImport]);
  const { data: importCandidatesRes, isLoading: importCandidatesLoading } = useSWR<unknown>(importCandidatesUrl, fetcher, {
    revalidateOnFocus: false,
  });
  const importCandidates = React.useMemo((): SpareRequisition[] => {
    if (!importCandidatesRes) return [];
    const raw = Array.isArray(importCandidatesRes)
      ? importCandidatesRes
      : (importCandidatesRes as { data?: SpareRequisition[] })?.data ?? [];
    return raw;
  }, [importCandidatesRes]);
  const importCandidatesSameVessel = React.useMemo((): SpareRequisition[] => {
    if (!vesselIdForImport) return [];
    const vesselIdN = Number(vesselIdForImport);
    return importCandidates.filter((r) => {
      const rid = r.vesselId != null ? Number(r.vesselId) : NaN;
      return Number.isFinite(rid) && rid === vesselIdN;
    });
  }, [importCandidates, vesselIdForImport]);

  const newRequisitionTo = React.useMemo(() => {
    const sc = data?.spareCase;
    if (!sc) return requisitionsModuleConfig.module.route.new;
    const q = new URLSearchParams();
    q.set('caseId', String(sc.id));
    if (sc.vessel?.id != null) q.set('vesselId', String(sc.vessel.id));
    return `${requisitionsModuleConfig.module.route.new}?${q.toString()}`;
  }, [data?.spareCase]);
  const [viewTab, setViewTab] = React.useState<CaseViewTab>('case');
  const [viewMode, setViewMode] = React.useState<ViewMode>('flat');
  const [postCreate, setPostCreate] = React.useState<'first' | 'stay'>('first');
  const [caseDetailsOpen, setCaseDetailsOpen] = React.useState(false);

  const kpi = React.useMemo(() => {
    if (!data) return null;
    const quotedEnq = new Set(data.quotations.map((q) => q.enqId));
    const openEnq = data.enquiries.filter((e) => !quotedEnq.has(e.id)).length;
    return {
      requisitions: data.requisitions.length,
      enquiries: data.enquiries.length,
      openEnquiries: openEnq,
      quotations: data.quotations.length,
      purchaseOrders: data.purchaseOrders.length,
      forwardings: data.forwardings?.length ?? 0,
      deliveries: data.deliveries.length,
      invoices: data.invoices.length,
    };
  }, [data]);

  const { data: pendingReqApprovalsRaw } = useSWR(
    caseViewKey ? '/approvals?inbox=all&status=PENDING&sourceModule=spare-requisitions&limit=100' : null,
    fetcher,
  );
  const { data: pendingPoApprovalsRaw } = useSWR(
    caseViewKey ? '/approvals?inbox=all&status=PENDING&sourceModule=spare-purchase-orders&limit=100' : null,
    fetcher,
  );

  const pendingReqIds = React.useMemo(() => {
    const caseIds = new Set((data?.requisitions || []).map((r) => r.id));
    const list = Array.isArray(pendingReqApprovalsRaw)
      ? pendingReqApprovalsRaw
      : normalizePaginatedListResponse<{ sourceEntityId?: number | null }>(pendingReqApprovalsRaw as any, 1, 100).data;
    return new Set(
      list
        .map((a) => a.sourceEntityId)
        .filter((id): id is number => typeof id === 'number' && caseIds.has(id)),
    );
  }, [data?.requisitions, pendingReqApprovalsRaw]);

  const pendingPoIds = React.useMemo(() => {
    const caseIds = new Set((data?.purchaseOrders || []).map((p) => p.id));
    const list = Array.isArray(pendingPoApprovalsRaw)
      ? pendingPoApprovalsRaw
      : normalizePaginatedListResponse<{ sourceEntityId?: number | null }>(pendingPoApprovalsRaw as any, 1, 100).data;
    return new Set(
      list
        .map((a) => a.sourceEntityId)
        .filter((id): id is number => typeof id === 'number' && caseIds.has(id)),
    );
  }, [data?.purchaseOrders, pendingPoApprovalsRaw]);

  const allRows = React.useMemo(() => {
    const rows = buildRows(data);
    return rows.map((row) => ({
      ...row,
      pendingApproval:
        (row.kind === 'Requisition' && pendingReqIds.has(row.id)) ||
        (row.kind === 'Purchase order' && pendingPoIds.has(row.id)),
    }));
  }, [data, pendingReqIds, pendingPoIds]);
  const sortedRows = React.useMemo(() => sortActivityRowsNewestFirst(allRows), [allRows]);
  const stageGroups = React.useMemo(() => groupRowsByStage(sortedRows), [sortedRows]);

  const [enquiryOpen, setEnquiryOpen] = React.useState(false);
  const [enquiryRequisitionId, setEnquiryRequisitionId] = React.useState<number | null>(null);
  const [enquirySupplierIds, setEnquirySupplierIds] = React.useState<Array<string | number>>([]);
  const [enquiryNotes, setEnquiryNotes] = React.useState('');
  const [enquiryBusy, setEnquiryBusy] = React.useState(false);
  const [quotationBusyEnquiryId, setQuotationBusyEnquiryId] = React.useState<number | null>(null);
  const [purchaseOrderBusyQuotationId, setPurchaseOrderBusyQuotationId] = React.useState<number | null>(null);
  const [deliveryBusyPurchaseOrderId, setDeliveryBusyPurchaseOrderId] = React.useState<number | null>(null);
  const [deletingRowKey, setDeletingRowKey] = React.useState<string | null>(null);
  const [deleteConfirmRow, setDeleteConfirmRow] = React.useState<{
    id: number;
    kind: string;
    ref: string;
  } | null>(null);
  const prevSupplierPickN = React.useRef(0);
  React.useEffect(() => {
    const n = enquirySupplierIds.length;
    if (n > 1 && prevSupplierPickN.current <= 1) setPostCreate('stay');
    if (n <= 1 && prevSupplierPickN.current > 1) setPostCreate('first');
    prevSupplierPickN.current = n;
  }, [enquirySupplierIds.length]);

  const { data: suppliersRes } = useSWR<any>('/suppliers?page=1&limit=500', fetcher);
  const supplierMultiOptions = React.useMemo(() => {
    const list = Array.isArray(suppliersRes) ? suppliersRes : suppliersRes?.data ?? [];
    return (list as { id: number; name: string; code?: string | null }[]).map((s) => ({
      value: String(s.id),
      label: s.name,
    }));
  }, [suppliersRes]);

  const openEnquiryForRequisition = (requisitionId: number) => {
    setEnquiryRequisitionId(requisitionId);
    setEnquirySupplierIds([]);
    setEnquiryNotes('');
    setPostCreate('first');
    prevSupplierPickN.current = 0;
    setEnquiryOpen(true);
  };

  const enquiryReqLabel = React.useMemo(() => {
    if (enquiryRequisitionId == null || !data) return null;
    const r = data.requisitions.find((x) => x.id === enquiryRequisitionId);
    return r ? r.ref || `R-${r.id}` : null;
  }, [enquiryRequisitionId, data]);

  const enquiryReqSaveStatus = React.useMemo(() => {
    if (enquiryRequisitionId == null || !data) return null;
    return data.requisitions.find((x) => x.id === enquiryRequisitionId)?.saveStatus ?? null;
  }, [enquiryRequisitionId, data]);

  const enquiryCountByRequisitionId = React.useMemo(() => {
    const map = new Map<number, number>();
    for (const e of data?.enquiries ?? []) {
      if (e.requisitionId == null) continue;
      map.set(e.requisitionId, (map.get(e.requisitionId) ?? 0) + 1);
    }
    return map;
  }, [data?.enquiries]);

  const requisitionIdsWithPurchaseOrder = React.useMemo(
    () => (data ? buildRequisitionIdsWithPurchaseOrder(data) : new Set<number>()),
    [data],
  );

  const copyActivityRowSummary = React.useCallback(
    async (row: TableRow) => {
      const line = [row.kind, row.ref, row.dateStr, row.detail].filter(Boolean).join('\t');
      const ok = await copyTextToClipboard(line);
      if (ok) showSuccess('Row copied to clipboard');
      else showError('Could not copy to clipboard');
    },
    [showError, showSuccess],
  );

  const mergeableRequisitions = React.useMemo(() => {
    if (!data?.requisitions?.length) return [];
    return data.requisitions.filter((r) => !requisitionIdsWithPurchaseOrder.has(r.id));
  }, [data?.requisitions, requisitionIdsWithPurchaseOrder]);

  /** Requisitions that cannot be removed from this case (unlink) — linked via enquiry or quotation on this case payload. */
  const requisitionIdsBlockedFromCaseRemoval = React.useMemo(() => {
    const ids = new Set<number>();
    if (!data) return ids;
    for (const e of data.enquiries) {
      if (e.requisitionId != null) ids.add(e.requisitionId);
    }
    for (const q of data.quotations) {
      if (q.requisitionId != null) ids.add(q.requisitionId);
      const enq = data.enquiries.find((x) => x.id === q.enqId);
      if (enq?.requisitionId != null) ids.add(enq.requisitionId);
    }
    return ids;
  }, [data]);

  const combineSubmitMode = React.useMemo(() => {
    const ids = combineReqIds
      .map((v) => Number(v))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (ids.length < 2) return 'none' as const;
    const withEnq = ids.filter((id) => (enquiryCountByRequisitionId.get(id) ?? 0) > 0);
    if (withEnq.length > 1) return 'blockedMultipleRfq' as const;
    if (withEnq.length === 1) return 'importIntoRfq' as const;
    return 'createDraft' as const;
  }, [combineReqIds, enquiryCountByRequisitionId]);

  const quotationCountByEnquiryId = React.useMemo(() => {
    const map = new Map<number, number>();
    for (const q of data?.quotations ?? []) {
      if (q.enqId == null) continue;
      map.set(q.enqId, (map.get(q.enqId) ?? 0) + 1);
    }
    return map;
  }, [data?.quotations]);

  const purchaseOrderCountByQuotationId = React.useMemo(() => {
    const map = new Map<number, number>();
    for (const po of data?.purchaseOrders ?? []) {
      if (po.quotationId == null) continue;
      map.set(po.quotationId, (map.get(po.quotationId) ?? 0) + 1);
    }
    return map;
  }, [data?.purchaseOrders]);

  const deliveryCountByPurchaseOrderId = React.useMemo(() => {
    const map = new Map<number, number>();
    for (const d of data?.deliveries ?? []) {
      if (d.purchaseOrderId == null) continue;
      map.set(d.purchaseOrderId, (map.get(d.purchaseOrderId) ?? 0) + 1);
    }
    return map;
  }, [data?.deliveries]);

  const invoiceCountByDeliveryId = React.useMemo(() => {
    const map = new Map<number, number>();
    for (const inv of data?.invoices ?? []) {
      if (inv.deliveryId == null) continue;
      map.set(inv.deliveryId, (map.get(inv.deliveryId) ?? 0) + 1);
    }
    return map;
  }, [data?.invoices]);

  const evaluationCountByQuotationId = React.useMemo(() => {
    const map = new Map<number, number>();
    for (const ev of data?.evaluations ?? []) {
      const quotationIds = [ev.quotation1Id, ev.quotation2Id, ev.quotation3Id, ev.quotation4Id, ev.quotation5Id];
      for (const qid of quotationIds) {
        if (qid == null) continue;
        map.set(qid, (map.get(qid) ?? 0) + 1);
      }
    }
    return map;
  }, [data?.evaluations]);

  React.useEffect(() => {
    if (!enquiryOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !enquiryBusy) setEnquiryOpen(false);
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [enquiryOpen, enquiryBusy]);

  if (!Number.isFinite(id)) {
    return <p className={cn('text-sm', colors.textSecondary)}>Invalid case id.</p>;
  }

  if (isLoading) {
    return <p className={cn('text-sm', colors.textSecondary)}>Loading…</p>;
  }

  if (error || !item) {
    return (
      <div className="space-y-3">
        <p className={cn('text-sm', colors.textSecondary)}>This spare case was not found.</p>
        <button
          type="button"
          onClick={goBack}
          className="text-sm font-medium text-indigo-600 hover:underline"
        >
          Back
        </button>
      </div>
    );
  }

  const deleteLabelForKind = (kind: string) => {
    if (kind === 'Requisition') return 'requisition';
    if (kind === 'Supplier enquiry') return 'RFQ';
    if (kind === 'Purchase order') return 'order';
    if (kind === 'Forwarding') return 'forwarding';
    return kind.toLowerCase();
  };

  const performDelete = async (row: { id: number; kind: string; ref: string }) => {
    const rowDeleteKey = `${row.kind}-${row.id}`;
    const deleteLabel = deleteLabelForKind(row.kind);
    try {
      setDeletingRowKey(rowDeleteKey);
      if (row.kind === 'Requisition') {
        await unlinkSpareRequisitionFromCase(row.id);
      } else if (row.kind === 'Supplier enquiry') {
        await deleteSpareSupplierEnquiry(row.id);
      } else if (row.kind === 'Quotation') {
        await deleteSpareSupplierQuotation(row.id);
      } else if (row.kind === 'Evaluation') {
        await deleteSpareQuotationEvaluation(row.id);
      } else if (row.kind === 'Purchase order') {
        await deleteSparePurchaseOrder(row.id);
      } else if (row.kind === 'Delivery') {
        await deleteSpareDelivery(row.id);
      } else if (row.kind === 'Forwarding') {
        await deleteSpareForwarding(row.id);
      } else if (row.kind === 'Invoice') {
        await deleteSpareInvoice(row.id);
      } else {
        showError('Delete is not configured for this type.');
        return;
      }
      await revalidateCaseView();
      showSuccess(
        row.kind === 'Requisition'
          ? 'Requisition removed from this case (record kept in requisitions).'
          : `${row.kind} deleted.`,
      );
    } catch (err: unknown) {
      const data =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string | string[] } } }).response?.data
          : undefined;
      const m = data?.message;
      const text = Array.isArray(m) ? m.join('; ') : typeof m === 'string' ? m : `Could not delete ${deleteLabel}.`;
      showError(text);
    } finally {
      setDeletingRowKey(null);
      setDeleteConfirmRow(null);
    }
  };

  const renderActivityRow = (row: TableRow, opts?: { grouped?: boolean }) => {
    const grouped = opts?.grouped === true;
    const isReq = row.kind === 'Requisition';
    const isEnquiry = row.kind === 'Supplier enquiry';
    const isQuotation = row.kind === 'Quotation';
    const isPurchaseOrder = row.kind === 'Purchase order';
    const isForwarding = row.kind === 'Forwarding';
    const isDelivery = row.kind === 'Delivery';
    const isInvoice = row.kind === 'Invoice';
    const quotationBusy = quotationBusyEnquiryId === row.id;
    const purchaseOrderBusy = purchaseOrderBusyQuotationId === row.id;
    const enquiryQuotationCount = isEnquiry ? quotationCountByEnquiryId.get(row.id) ?? 0 : 0;
    const canCreateQuotationFromEnquiry = isEnquiry ? enquiryQuotationCount === 0 : false;
    const quotationPurchaseOrderCount = isQuotation ? purchaseOrderCountByQuotationId.get(row.id) ?? 0 : 0;
    const canDirectPoFromQuotation = isQuotation ? quotationPurchaseOrderCount === 0 : false;
    const showCreateDeliveryButton = isPurchaseOrder && item != null;
    const purchaseOrderDeliveryCount = isPurchaseOrder ? deliveryCountByPurchaseOrderId.get(row.id) ?? 0 : 0;
    const canCreateDeliveryFromPurchaseOrder = showCreateDeliveryButton && purchaseOrderDeliveryCount === 0;
    const purchaseOrderForwarding =
      isPurchaseOrder && data?.purchaseOrders
        ? data.purchaseOrders.find((p) => p.id === row.id)?.forwarding ?? null
        : null;
    const createInvoiceTo =
      isDelivery && item != null && row.deliveryPurchaseOrderId != null
        ? `${supplyInvoicesConfig.module.route.new}?purchaseOrderId=${row.deliveryPurchaseOrderId}&deliveryId=${row.id}`
        : null;
    const invoiceCountForDelivery = isDelivery ? invoiceCountByDeliveryId.get(row.id) ?? 0 : 0;
    const canCreateInvoiceFromDelivery = createInvoiceTo != null && invoiceCountForDelivery === 0;
    const rowDeleteKey = `${row.kind}-${row.id}`;
    const financialDetail = isQuotation ? row.quotationDetail : isPurchaseOrder ? row.purchaseOrderDetail : isInvoice ? row.invoiceDetail : undefined;
    const financialEditHref = isQuotation
      ? supplierQuotationConfig.module.route.edit(row.id)
      : isPurchaseOrder
        ? purchaseOrdersConfig.module.route.edit(row.id)
        : isInvoice
          ? supplyInvoicesConfig.module.route.edit(row.id)
          : null;
    const isDeleting = deletingRowKey === rowDeleteKey;
    const deleteBlockedReason = (() => {
      if (isReq) {
        if (requisitionIdsBlockedFromCaseRemoval.has(row.id)) {
          return 'Cannot remove from case: a supplier enquiry or quotation on this case is linked to this requisition.';
        }
      }
      if (isEnquiry) {
        const quoCount = quotationCountByEnquiryId.get(row.id) ?? 0;
        if (quoCount > 0) {
          return `Cannot delete RFQ: ${quoCount} quotation${quoCount === 1 ? '' : 's'} exist for this RFQ.`;
        }
      }
      if (isQuotation) {
        const evalCount = evaluationCountByQuotationId.get(row.id) ?? 0;
        if (evalCount > 0) {
          return `Cannot delete quotation: used in ${evalCount} evaluation${evalCount === 1 ? '' : 's'}.`;
        }
        const poCount = purchaseOrderCountByQuotationId.get(row.id) ?? 0;
        if (poCount > 0) {
          return `Cannot delete quotation: linked to ${poCount} order${poCount === 1 ? '' : 's'}.`;
        }
      }
      if (isPurchaseOrder) {
        const dlvCount = deliveryCountByPurchaseOrderId.get(row.id) ?? 0;
        if (dlvCount > 0) {
          return `Cannot delete order: linked to ${dlvCount} ${dlvCount === 1 ? 'delivery' : 'deliveries'}.`;
        }
      }
      if (isForwarding && row.forwardingDetail != null && row.forwardingDetail.deliveryCount > 0) {
        const n = row.forwardingDetail.deliveryCount;
        return `Cannot delete forwarding: linked to ${n} ${n === 1 ? 'delivery' : 'deliveries'}.`;
      }
      return null;
    })();
    const canDelete = deleteBlockedReason == null;
    const runDelete = async () => {
      if (!canDelete) {
        showError(deleteBlockedReason ?? 'Delete is blocked by linked records.');
        return;
      }
      setDeleteConfirmRow({ id: row.id, kind: row.kind, ref: row.ref });
    };
    return (
      <tr
        key={`${row.kind}-${row.id}`}
        className={cn(
          'border-b transition-colors last:border-0',
          colors.border,
          'hover:bg-gray-50/90 dark:hover:bg-white/[0.04]',
        )}
      >
        {!grouped ? (
          <td className="align-top px-4 py-2.5 md:pl-5">
            <span className={kindBadgeClass(row.kind)}>{row.kind}</span>
          </td>
        ) : null}
        <td className={cn('align-top whitespace-nowrap py-2.5 pr-2', grouped ? 'pl-4 md:pl-5' : 'pl-0')}>
          {isReq ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 shrink-0 gap-1.5 rounded-lg px-2.5 text-indigo-600 dark:text-indigo-400"
              title="Create supplier enquiry from this requisition"
              onClick={() => openEnquiryForRequisition(row.id)}
            >
              <DocumentTextIcon className="h-4 w-4 shrink-0" />
              Create Enquiry
            </Button>
          ) : isEnquiry ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 shrink-0 gap-1.5 rounded-lg px-2.5 text-amber-700 dark:text-amber-300"
              title={
                canCreateQuotationFromEnquiry
                  ? 'Create quotation from this enquiry'
                  : 'Quotation already exists for this enquiry'
              }
              loading={quotationBusy}
              disabled={quotationBusyEnquiryId != null || !canCreateQuotationFromEnquiry}
              onClick={async () => {
                if (!canCreateQuotationFromEnquiry) return;
                setQuotationBusyEnquiryId(row.id);
                try {
                  const created = await createSpareSupplierQuotationFromEnquiry({ enquiryId: row.id });
                  await revalidateCaseView();
                  showSuccess('Quotation created.');
                  if (created?.id != null) {
                    navigate(supplierQuotationConfig.module.route.edit(created.id));
                  }
                } catch (err: unknown) {
                  const data =
                    err && typeof err === 'object' && 'response' in err
                      ? (err as { response?: { data?: { message?: string | string[] } } }).response?.data
                      : undefined;
                  const m = data?.message;
                  const text = Array.isArray(m)
                    ? m.join('; ')
                    : typeof m === 'string'
                      ? m
                      : 'Could not create quotation from enquiry.';
                  showError(text);
                } finally {
                  setQuotationBusyEnquiryId(null);
                }
              }}
            >
              <DocumentTextIcon className="h-4 w-4 shrink-0" />
              Create Quotation
            </Button>
          ) : isQuotation ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              loading={purchaseOrderBusy}
              disabled={purchaseOrderBusyQuotationId != null || !canDirectPoFromQuotation}
              className="h-9 shrink-0 gap-1.5 rounded-lg px-2.5 text-emerald-700 dark:text-emerald-300"
              title={
                canDirectPoFromQuotation
                  ? 'Create purchase order from this quotation'
                  : 'Purchase order already exists for this quotation'
              }
              onClick={async () => {
                if (!canDirectPoFromQuotation) return;
                setPurchaseOrderBusyQuotationId(row.id);
                try {
                  const created = await createSparePurchaseOrderFromQuotation({
                    quotationId: row.id,
                  });
                  await revalidateCaseView();
                  showSuccess('Purchase order created.');
                  if (created?.id != null) {
                    navigate(purchaseOrdersConfig.module.route.edit(created.id), {
                      state: { from: `${location.pathname}${location.search}` },
                    });
                  }
                } catch (err: unknown) {
                  const data =
                    err && typeof err === 'object' && 'response' in err
                      ? (err as { response?: { data?: { message?: string | string[] } } }).response?.data
                      : undefined;
                  const m = data?.message;
                  const text = Array.isArray(m)
                    ? m.join('; ')
                    : typeof m === 'string'
                      ? m
                      : 'Could not create purchase order from quotation.';
                  showError(text);
                } finally {
                  setPurchaseOrderBusyQuotationId(null);
                }
              }}
            >
              <DocumentTextIcon className="h-4 w-4 shrink-0" />
              Direct PO
            </Button>
          ) : showCreateDeliveryButton ? (
            <div className="flex flex-col items-start gap-1.5">
              {item != null ? (
                purchaseOrderForwarding ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled
                    className="h-9 shrink-0 cursor-not-allowed gap-1.5 opacity-60"
                    title="Forwarding already exists for this order. Use the Forwarding stage row (Open) or the activity list to view it."
                  >
                    <TableIcon className="h-4 w-4 shrink-0" />
                    Create forwarding
                  </Button>
                ) : (
                  <Link
                    to={`${supplyForwardingsConfig.module.route.new}?caseId=${item.id}&purchaseOrderId=${row.id}`}
                    state={{ from: `${location.pathname}${location.search}` }}
                    className="inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-sky-200/90 bg-sky-50/80 px-2.5 text-sm font-medium text-sky-900 transition-colors hover:bg-sky-100 dark:border-sky-800/60 dark:bg-sky-950/35 dark:text-sky-100 dark:hover:bg-sky-900/40"
                    title="Record shipment / port tracking before creating a delivery (optional)"
                  >
                    <TableIcon className="h-4 w-4 shrink-0" />
                    Create forwarding
                  </Link>
                )
              ) : null}
              <Button
                type="button"
                variant="outline"
                size="sm"
                loading={deliveryBusyPurchaseOrderId === row.id}
                disabled={deliveryBusyPurchaseOrderId != null || !canCreateDeliveryFromPurchaseOrder}
                className="h-9 shrink-0 gap-1.5 rounded-lg px-2.5 text-emerald-700 dark:text-emerald-300"
                title={
                  canCreateDeliveryFromPurchaseOrder
                    ? 'Create delivery from this purchase order'
                    : 'Delivery already exists for this purchase order'
                }
                onClick={async () => {
                  if (!canCreateDeliveryFromPurchaseOrder) return;
                  setDeliveryBusyPurchaseOrderId(row.id);
                  try {
                    const created = await createSpareDeliveryFromPurchaseOrder({
                      purchaseOrderId: row.id,
                      ...(item?.vessel?.id != null ? { vesselId: item.vessel.id } : {}),
                      ...(item?.id != null ? { caseId: item.id } : {}),
                      ...(purchaseOrderForwarding?.id != null ? { supplyForwardingId: purchaseOrderForwarding.id } : {}),
                    });
                    await revalidateCaseView();
                    showSuccess('Delivery created.');
                    if (created?.id != null) {
                      navigate(supplyDeliveriesConfig.module.route.edit(created.id));
                    }
                  } catch (err: unknown) {
                    const data =
                      err && typeof err === 'object' && 'response' in err
                        ? (err as { response?: { data?: { message?: string | string[] } } }).response?.data
                        : undefined;
                    const m = data?.message;
                    const text = Array.isArray(m)
                      ? m.join('; ')
                      : typeof m === 'string'
                        ? m
                        : 'Could not create delivery from purchase order.';
                    showError(text);
                  } finally {
                    setDeliveryBusyPurchaseOrderId(null);
                  }
                }}
              >
                <DocumentTextIcon className="h-4 w-4 shrink-0" />
                Create delivery
              </Button>
            </div>
          ) : isDelivery ? (
            <div className="flex flex-col items-start gap-1.5">
              {row.newRequisitionOutsideCaseHref ? (
                <Link
                  to={row.newRequisitionOutsideCaseHref}
                  className="inline-flex h-9 w-max min-w-0 max-w-full shrink-0 items-center justify-center gap-1.5 rounded-lg border border-amber-200/80 bg-amber-50/50 px-2.5 text-amber-900 transition-colors hover:bg-amber-100 dark:border-amber-800/60 dark:bg-amber-950/25 dark:text-amber-100 dark:hover:bg-amber-900/30"
                  title="New requisition with no case linked, importing shortfall / pending lines from this delivery"
                >
                  <DocumentTextIcon className="h-4 w-4 shrink-0" />
                  New requisition (no case)
                </Link>
              ) : null}
              {createInvoiceTo ? (
                canCreateInvoiceFromDelivery ? (
                  <Link
                    to={createInvoiceTo}
                    className="inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg border px-2.5 text-emerald-700 transition-colors hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-900/30"
                    title="Create invoice from this delivery"
                  >
                    <DocumentTextIcon className="h-4 w-4 shrink-0" />
                    Create Invoice
                  </Link>
                ) : (
                  <span
                    role="button"
                    aria-disabled="true"
                    className={cn(
                      'inline-flex h-9 shrink-0 cursor-not-allowed items-center justify-center gap-1.5 rounded-lg border px-2.5 opacity-50',
                      colors.border,
                      colors.textSecondary,
                    )}
                    title="Invoice already exists for this delivery"
                  >
                    <DocumentTextIcon className="h-4 w-4 shrink-0" />
                    Create Invoice
                  </span>
                )
              ) : null}
            </div>
          ) : null}
        </td>
        <td
          className={cn(
            'align-top truncate px-2 py-2.5 font-medium tabular-nums',
            grouped ? 'min-w-0 max-w-full' : 'max-w-[14rem]',
          )}
          title={row.ref}
        >
          {row.ref}
        </td>
        <td
          className={cn('align-top whitespace-nowrap px-2 py-2.5 tabular-nums', colors.textSecondary)}
        >
          {row.dateStr}
        </td>
        <td
          className={cn(
            'align-top py-2.5 pr-2',
            grouped ? 'min-w-0' : 'min-w-[18rem] sm:min-w-[24rem] lg:min-w-[32rem]',
            activityDetailsRailClass(row.kind),
            colors.text,
          )}
        >
          {isReq ? (
            <div className="flex flex-wrap items-center gap-2">
              {row.requisitionSaveStatus ? (
                <Badge
                  variant="secondary"
                  className={cn(
                    'shrink-0',
                    row.requisitionSaveStatus.trim().toUpperCase() === 'SAVED'
                      ? 'rounded-md border border-emerald-200/90 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold tracking-wide text-emerald-950 dark:border-emerald-800 dark:bg-emerald-950/35 dark:text-emerald-100'
                      : undefined,
                  )}
                >
                  {formatRequisitionSaveStatusLabel(row.requisitionSaveStatus)}
                </Badge>
              ) : null}
              {row.pendingApproval ? (
                <Badge
                  variant="warning"
                  className="shrink-0 rounded-md px-2 py-0.5 text-[11px] font-semibold tracking-wide"
                >
                  Pending approval
                </Badge>
              ) : null}
              {row.detail ? (
                <div
                  className="line-clamp-2 min-w-0 flex-1 break-words text-sm leading-6 md:text-base"
                  title={row.detail}
                >
                  {row.detail}
                </div>
              ) : null}
            </div>
          ) : financialDetail ? (
            <div className="space-y-1.5" title={row.detail}>
              <div className="break-words text-sm leading-6 md:text-base">
                <span className={cn('font-medium', colors.text)}>Supplier:</span>{' '}
                <span>{financialDetail.supplierLabel}</span>
                <span className={cn(colors.textSecondary)}> · {row.ref}</span>
              </div>
              {isPurchaseOrder && row.purchaseOrderDetail ? (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="inline-flex h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400 dark:bg-slate-500" aria-hidden />
                  <Link
                    className={ACTIVITY_REL_PILL}
                    to={supplierQuotationConfig.module.route.detail(row.purchaseOrderDetail.quotationId)}
                  >
                    {row.purchaseOrderDetail.quotationLinkLabel}
                  </Link>
                  {row.pendingApproval ? (
                    <Badge
                      variant="warning"
                      className="shrink-0 rounded-md px-2 py-0.5 text-[11px] font-semibold tracking-wide"
                    >
                      Pending approval
                    </Badge>
                  ) : null}
                </div>
              ) : null}
              {isInvoice && row.invoiceDetail ? (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  {row.invoiceDetail.purchaseOrderId != null ? (
                    <span className="inline-flex items-center gap-1.5">
                      <span className="inline-flex h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400 dark:bg-slate-500" aria-hidden />
                      <Link
                        className={ACTIVITY_REL_PILL}
                        to={purchaseOrdersConfig.module.route.detail(row.invoiceDetail.purchaseOrderId)}
                      >
                        {row.invoiceDetail.purchaseOrderLinkLabel}
                      </Link>
                    </span>
                  ) : null}
                  {row.invoiceDetail.deliveryId != null ? (
                    <span className="inline-flex items-center gap-1.5">
                      <span className="inline-flex h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400 dark:bg-slate-500" aria-hidden />
                      <Link
                        className={ACTIVITY_REL_PILL}
                        to={supplyDeliveriesConfig.module.route.detail(row.invoiceDetail.deliveryId)}
                      >
                        {row.invoiceDetail.deliveryLinkLabel}
                      </Link>
                    </span>
                  ) : null}
                </div>
              ) : null}
              <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
                <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs leading-tight md:text-[13px]">
                  {financialDetail.currencyLabel ? (
                    <span
                      className={cn(
                        'rounded-md border px-2 py-0.5 text-xs font-semibold tracking-wide',
                        colors.border,
                        'bg-gray-50 dark:bg-white/[0.06]',
                      )}
                    >
                      {financialDetail.currencyLabel}
                    </span>
                  ) : null}
                  {financialDetail.rate != null ? (
                    <span
                      className={cn(
                        'rounded-md border px-2 py-0.5 text-xs font-semibold tracking-wide tabular-nums',
                        colors.border,
                        'bg-gray-50 dark:bg-white/[0.06]',
                      )}
                    >
                      Rate <span className={cn('font-medium', colors.text)}>{financialDetail.rate}</span>
                    </span>
                  ) : null}
                  {isQuotation && row.quotationDetail?.updatedDateStr ? (
                    <span
                      className={cn(
                        'rounded-md border px-2 py-0.5 text-xs font-semibold tracking-wide',
                        colors.border,
                        'bg-gray-50 dark:bg-white/[0.06]',
                        colors.textSecondary,
                      )}
                    >
                      Updated {row.quotationDetail.updatedDateStr}
                    </span>
                  ) : null}
                  {isQuotation && row.quotationDetail ? (
                    <span className="inline-flex items-center gap-1">
                      <span
                        className={cn(
                          'rounded-md border px-2 py-0.5 text-xs font-semibold tracking-wide',
                          colors.border,
                          (row.quotationDetail.portalOfferStatus ?? 'pending') === 'submitted'
                            ? 'bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100'
                            : 'bg-amber-50 text-amber-950 dark:bg-amber-950/35 dark:text-amber-100',
                          colors.textSecondary,
                        )}
                      >
                        {portalOfferStatusCompactLabel(row.quotationDetail.portalOfferStatus ?? 'pending')}
                      </span>
                      <button
                        type="button"
                        className={cn(
                          'inline-flex shrink-0 rounded-full p-0.5 outline-none transition-colors',
                          colors.textSecondary,
                          'hover:bg-slate-200/80 hover:text-slate-800 dark:hover:bg-slate-700/80 dark:hover:text-slate-100',
                          'focus-visible:ring-2 focus-visible:ring-slate-400/80 focus-visible:ring-offset-1',
                        )}
                        title={quotationPortalStatusTooltip(
                          row.quotationDetail.portalOfferStatus ?? 'pending',
                          row.quotationDetail.rfqPortalLastOpenedAt,
                          row.quotationDetail.rfqPortalLastSavedAt,
                          row.quotationDetail.rfqPublicSubmittedAt,
                        )}
                        aria-label="Supplier portal status: what this means and timestamps"
                      >
                        <InfoIcon className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  ) : null}
                  {isPurchaseOrder && row.purchaseOrderDetail?.updatedDateStr ? (
                    <span
                      className={cn(
                        'rounded-md border px-2 py-0.5 text-xs font-semibold tracking-wide',
                        colors.border,
                        'bg-gray-50 dark:bg-white/[0.06]',
                        colors.textSecondary,
                      )}
                    >
                      Updated {row.purchaseOrderDetail.updatedDateStr}
                    </span>
                  ) : null}
                  {isInvoice && row.invoiceDetail?.updatedDateStr ? (
                    <span
                      className={cn(
                        'rounded-md border px-2 py-0.5 text-xs font-semibold tracking-wide',
                        colors.border,
                        'bg-gray-50 dark:bg-white/[0.06]',
                        colors.textSecondary,
                      )}
                    >
                      Updated {row.invoiceDetail.updatedDateStr}
                    </span>
                  ) : null}
                </div>
                <div
                  className={cn(
                    'flex min-w-0 shrink-0 flex-col gap-1 text-xs tabular-nums leading-tight md:text-[13px]',
                    colors.text,
                  )}
                >
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className={cn('font-semibold', colors.textSecondary)}>
                      Total ({financialDetail.currencyCode?.trim() || 'local'})
                    </span>
                    <span className="font-medium">
                      {(() => {
                        const sym = (financialDetail.currencySymbol ?? '').trim();
                        const raw = financialDetail.totalLocal;
                        if (raw == null) return <span className={cn(colors.textSecondary)}>—</span>;
                        const amt = fmtActivityMoney(raw);
                        return sym ? `${sym}\u00A0${amt}` : amt;
                      })()}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className="font-semibold text-emerald-900 dark:text-emerald-300">Total (USD)</span>
                    <span className="font-medium text-emerald-950 dark:text-emerald-100">
                      {!activityFxRatePresent(financialDetail.rate) ? (
                        <span className="inline-flex flex-col items-start gap-0.5">
                          <span className={cn(colors.textSecondary)} title="No exchange rate on document — USD not converted.">
                            —
                          </span>
                          {financialEditHref ? (
                            <Link
                              to={financialEditHref}
                              state={{ from: `${location.pathname}${location.search}` }}
                              className={ACTIVITY_FINANCIAL_EDIT_LINK}
                            >
                              Set rate
                            </Link>
                          ) : null}
                        </span>
                      ) : financialDetail.totalUsd != null &&
                        financialDetail.totalUsd !== '' &&
                        Number.isFinite(Number(financialDetail.totalUsd)) ? (
                        `$ ${fmtActivityMoney(financialDetail.totalUsd)}`
                      ) : (
                        <span className="inline-flex flex-col items-start gap-0.5">
                          <span
                            className={cn(colors.textSecondary, 'cursor-help opacity-85')}
                            title="USD total unavailable from lines and rate."
                          >
                            No FX
                          </span>
                          {financialEditHref ? (
                            <Link
                              to={financialEditHref}
                              state={{ from: `${location.pathname}${location.search}` }}
                              className={ACTIVITY_FINANCIAL_EDIT_LINK}
                            >
                              Review document
                            </Link>
                          ) : null}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : row.evaluationDetail ? (
            <div className="space-y-1.5" title={row.detail}>
              <div className="break-words text-sm leading-6 md:text-base">
                <span className={cn('font-medium', colors.text)}>
                  {row.evaluationDetail.quotations.length > 0
                    ? `${row.evaluationDetail.quotations.length} quotation${row.evaluationDetail.quotations.length === 1 ? '' : 's'} compared`
                    : 'No quotations linked'}
                </span>
                <span className={cn(colors.textSecondary)}> · {row.ref}</span>
              </div>
              {row.evaluationDetail.quotations.length > 0 ? (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  {row.evaluationDetail.quotations.map((q) => (
                    <span key={q.id} className="inline-flex items-center gap-1.5">
                      <span className="inline-flex h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400 dark:bg-slate-500" aria-hidden />
                      <Link
                        className={ACTIVITY_REL_PILL}
                        to={supplierQuotationConfig.module.route.detail(q.id)}
                        title={q.supplierLabel}
                      >
                        {q.refLabel}
                      </Link>
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          ) : row.forwardingDetail ? (
            <div className="space-y-1.5" title={row.detail}>
              <div className="break-words text-sm leading-6 md:text-base">
                <span className={cn('font-medium', colors.text)}>Forwarder:</span>{' '}
                <span>{row.forwardingDetail.forwarderLabel}</span>
                <span className={cn(colors.textSecondary)}> · {row.ref}</span>
              </div>
              {row.forwardingDetail.purchaseOrderId != null ? (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="inline-flex h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400 dark:bg-slate-500" aria-hidden />
                  <Link
                    className={ACTIVITY_REL_PILL}
                    to={purchaseOrdersConfig.module.route.detail(row.forwardingDetail.purchaseOrderId)}
                  >
                    {row.forwardingDetail.purchaseOrderLinkLabel}
                  </Link>
                </div>
              ) : null}
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs leading-tight md:text-[13px]">
                {row.forwardingDetail.portLabel ? (
                  <span
                    className={cn(
                      'rounded-md border px-2 py-0.5 text-xs font-semibold tracking-wide',
                      colors.border,
                      'bg-gray-50 dark:bg-white/[0.06]',
                      colors.textSecondary,
                    )}
                  >
                    {row.forwardingDetail.portLabel}
                  </span>
                ) : null}
                {row.forwardingDetail.statusLabel ? (
                  <span
                    className={cn(
                      'rounded-md border px-2 py-0.5 text-xs font-semibold tracking-wide',
                      colors.border,
                      'bg-gray-50 dark:bg-white/[0.06]',
                      colors.textSecondary,
                    )}
                  >
                    {row.forwardingDetail.statusLabel}
                  </span>
                ) : null}
                {row.forwardingDetail.priorityLabel ? (
                  <span
                    className={cn(
                      'rounded-md border px-2 py-0.5 text-xs font-semibold tracking-wide',
                      colors.border,
                      'bg-gray-50 dark:bg-white/[0.06]',
                      colors.textSecondary,
                    )}
                  >
                    {row.forwardingDetail.priorityLabel}
                  </span>
                ) : null}
                {row.forwardingDetail.warehouseLocationLabel ? (
                  <span
                    className={cn(
                      'rounded-md border px-2 py-0.5 text-xs font-semibold tracking-wide',
                      colors.border,
                      'bg-gray-50 dark:bg-white/[0.06]',
                      colors.textSecondary,
                    )}
                    title="Warehouse location"
                  >
                    WH {row.forwardingDetail.warehouseLocationLabel}
                  </span>
                ) : null}
                {row.forwardingDetail.costUsdStr ? (
                  <span
                    className={cn(
                      'rounded-md border px-2 py-0.5 text-xs font-semibold tracking-wide tabular-nums',
                      colors.border,
                      'bg-gray-50 dark:bg-white/[0.06]',
                      colors.textSecondary,
                    )}
                  >
                    USD {row.forwardingDetail.costUsdStr}
                  </span>
                ) : null}
                {row.forwardingDetail.etaDateStr ? (
                  <span
                    className={cn(
                      'rounded-md border px-2 py-0.5 text-xs font-semibold tracking-wide tabular-nums',
                      colors.border,
                      'bg-gray-50 dark:bg-white/[0.06]',
                      colors.textSecondary,
                    )}
                  >
                    ETA {row.forwardingDetail.etaDateStr}
                  </span>
                ) : null}
              </div>
            </div>
          ) : row.enquiryDetail ? (
            <div className="space-y-1.5" title={row.detail}>
              <div className="break-words text-sm leading-6 md:text-base">
                <span className={cn('font-medium', colors.text)}>Supplier:</span>{' '}
                <span>{row.enquiryDetail.supplierLabel}</span>
                <span className={cn(colors.textSecondary)}> · {row.ref}</span>
              </div>
              {row.enquiryDetail.requisitionId != null ? (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="inline-flex h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400 dark:bg-slate-500" aria-hidden />
                  <Link
                    className={ACTIVITY_REL_PILL}
                    to={requisitionsModuleConfig.module.route.detail(row.enquiryDetail.requisitionId)}
                  >
                    {row.enquiryDetail.requisitionLinkLabel}
                  </Link>
                </div>
              ) : null}
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs leading-tight md:text-[13px]">
                {row.enquiryDetail.updatedDateStr ? (
                  <span
                    className={cn(
                      'rounded-md border px-2 py-0.5 text-xs font-semibold tracking-wide',
                      colors.border,
                      'bg-gray-50 dark:bg-white/[0.06]',
                      colors.textSecondary,
                    )}
                  >
                    Updated {row.enquiryDetail.updatedDateStr}
                  </span>
                ) : null}
                {enquiryQuotationCount > 0 ? (
                  <span
                    className={cn(
                      'rounded-md border px-2 py-0.5 text-xs font-semibold tracking-wide',
                      colors.border,
                      'bg-gray-50 dark:bg-white/[0.06]',
                      colors.textSecondary,
                    )}
                  >
                    {enquiryQuotationCount} quotation{enquiryQuotationCount === 1 ? '' : 's'}
                  </span>
                ) : null}
              </div>
            </div>
          ) : row.deliveryDetail ? (
            <div className="space-y-1.5" title={row.detail}>
              <div className="break-words text-sm leading-6 md:text-base">
                <span className={cn('font-medium', colors.text)}>Supplier:</span>{' '}
                <span>{row.deliveryDetail.supplierLabel}</span>
                <span className={cn(colors.textSecondary)}> · {row.ref}</span>
              </div>
              {row.deliveryDetail.purchaseOrderId != null ? (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="inline-flex h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400 dark:bg-slate-500" aria-hidden />
                  <Link
                    className={ACTIVITY_REL_PILL}
                    to={purchaseOrdersConfig.module.route.detail(row.deliveryDetail.purchaseOrderId)}
                  >
                    {row.deliveryDetail.purchaseOrderLinkLabel}
                  </Link>
                </div>
              ) : null}
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs leading-tight md:text-[13px]">
                {(() => {
                  const { linkedLines, pendingReceipt } = deliveryReceiptMetrics(data?.itemsActivity, row.id);
                  return (
                    <>
                      {linkedLines > 0 ? (
                        <span
                          className={cn(
                            'rounded-md border px-2 py-0.5 text-xs font-semibold tracking-wide tabular-nums',
                            colors.border,
                            'bg-gray-50 dark:bg-white/[0.06]',
                            colors.textSecondary,
                          )}
                        >
                          {linkedLines} line{linkedLines === 1 ? '' : 's'}
                        </span>
                      ) : null}
                      {pendingReceipt > 0 ? (
                        <span className="rounded-md border border-sky-200/90 bg-sky-50/90 px-2 py-0.5 text-xs font-semibold tracking-wide text-sky-950 dark:border-sky-800 dark:bg-sky-950/35 dark:text-sky-100">
                          {pendingReceipt} pending receipt
                        </span>
                      ) : null}
                    </>
                  );
                })()}
                {row.deliveryDetail.hasLineIssues ? (
                  <span className="rounded-md border border-amber-200/90 bg-amber-50/90 px-2 py-0.5 text-xs font-semibold tracking-wide text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
                    Receipt / qty issue
                  </span>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="line-clamp-2 break-words text-sm leading-6 md:text-base" title={row.detail}>
              {row.detail}
            </div>
          )}
          {isReq &&
          (requisitionIdsBlockedFromCaseRemoval.has(row.id) || requisitionIdsWithPurchaseOrder.has(row.id)) ? (
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {requisitionIdsBlockedFromCaseRemoval.has(row.id) ? (
                <span
                  className={cn(
                    'inline-flex max-w-full items-center rounded-md border border-l-[3px] px-2 py-1 text-[11px] font-medium leading-snug',
                    colors.border,
                    'border-l-violet-500 bg-violet-50/70 text-violet-950 dark:border-l-violet-400 dark:bg-violet-950/25 dark:text-violet-100',
                  )}
                  title="Remove from case is disabled while an enquiry or quotation on this case references this requisition."
                >
                  Cannot remove — Actions exist
                </span>
              ) : null}
              {requisitionIdsWithPurchaseOrder.has(row.id) ? (
                <span
                  className={cn(
                    'inline-flex max-w-full items-center rounded-md border border-l-[3px] px-2 py-1 text-[11px] font-medium leading-snug',
                    colors.border,
                    'border-l-rose-500 bg-rose-50/70 text-rose-950 dark:border-l-rose-400 dark:bg-rose-950/25 dark:text-rose-100',
                  )}
                  title="This requisition is tied to an order on this case. It is excluded from Merge requisitions until that order is no longer linked."
                >
                  Cannot merge — An order exists
                </span>
              ) : null}
            </div>
          ) : null}
          {row.contextExtra ? (
            <div className={cn('mt-1 text-sm', colors.textSecondary)}>{row.contextExtra}</div>
          ) : null}
        </td>
        <td className="align-top whitespace-nowrap py-2.5 pr-4 pl-0 text-right md:pr-5">
          <div className="inline-flex items-center justify-end gap-0.5">
            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-slate-600 transition-colors hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-white/[0.06]"
              title="Copy row summary"
              aria-label="Copy row summary"
              onClick={() => void copyActivityRowSummary(row)}
            >
              <CopyIcon className="h-5 w-5" />
            </button>
            {row.href ? (
              <Link
                to={row.href}
                {...(row.kind === 'Forwarding'
                  ? { state: { from: `${location.pathname}${location.search}` } as const }
                  : {})}
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-indigo-600 transition-colors hover:bg-indigo-100 dark:text-indigo-400 dark:hover:bg-indigo-900/40"
                title="Open record"
                aria-label="Open record"
              >
                <EyeIcon className="h-5 w-5" />
              </Link>
            ) : (
              <span
                className={cn('inline-flex h-10 w-10 items-center justify-center text-sm', colors.textSecondary, 'opacity-50')}
                title="No list view for this type"
              >
                <span className="sr-only">No list screen</span>—
              </span>
            )}
            <button
              type="button"
              onClick={runDelete}
              disabled={isDeleting}
              className={cn(
                'inline-flex h-10 w-10 items-center justify-center rounded-lg transition-colors',
                isDeleting
                  ? 'cursor-wait text-rose-400 dark:text-rose-300'
                  : canDelete
                    ? 'text-rose-700 hover:bg-rose-100 dark:text-rose-300 dark:hover:bg-rose-900/30'
                    : 'text-slate-400 hover:bg-slate-100 dark:text-slate-500 dark:hover:bg-slate-800/50',
              )}
              title={
                canDelete
                  ? isReq
                    ? 'Remove requisition from case'
                    : `Delete ${row.kind}`
                  : deleteBlockedReason ?? (isReq ? 'Remove requisition from case' : `Delete ${row.kind}`)
              }
              aria-label={isReq ? 'Remove requisition from case' : `Delete ${row.kind}`}
            >
              <XIcon className="h-4 w-4" />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-6">
      <Card
        variant="outlined"
        className={cn(
          'flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-5 sm:py-3.5',
          'border shadow-sm',
        )}
      >
        <div className="min-w-0">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn('-ml-2 gap-1.5 font-medium', colors.text)}
            onClick={goBack}
          >
            <ChevronLeftIcon className="h-4 w-4 opacity-80" />
            Back
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={quotationEvaluationsConfig.module.route.newForCase(item.id)}
            className={cn(
              'inline-flex h-10 items-center justify-center rounded-lg border px-4 py-2 text-sm font-medium shadow-sm',
              colors.border,
              colors.text,
            )}
          >
            New evaluation
          </Link>
          <Link
            to={moduleConfig.module.route.edit(item.id)}
            state={returnPath ? { from: returnPath } : undefined}
            className={cn(
              'inline-flex h-10 items-center justify-center rounded-lg px-4 py-2 text-sm font-medium shadow-sm',
              'bg-indigo-600 text-white hover:opacity-90',
            )}
          >
            Edit case
          </Link>
        </div>
      </Card>

      {kpi ? (
        <Card
          variant="outlined"
          className={cn('border p-4 shadow-sm sm:px-5', colors.borderLight)}
        >
          <h2
            className={cn('mb-3 text-xs font-semibold uppercase tracking-wider', colors.textSecondary)}
          >
            At a glance
          </h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
            {(
              [
                { label: 'Spare Requisition', value: kpi.requisitions },
                {
                  label: 'Enquiries',
                  value: kpi.enquiries,
                  sub:
                    kpi.enquiries === 0
                      ? undefined
                      : kpi.openEnquiries > 0
                        ? `${kpi.openEnquiries} awaiting quote`
                        : 'All have quotes',
                },
                { label: 'Quotations', value: kpi.quotations },
                { label: 'Purchase orders', value: kpi.purchaseOrders },
                { label: 'Forwardings', value: kpi.forwardings },
                { label: 'Deliveries', value: kpi.deliveries },
                { label: 'Invoices', value: kpi.invoices },
              ] as { label: string; value: number; sub?: string }[]
            ).map((s) => (
              <div
                key={s.label}
                className={cn('rounded-lg border px-3 py-2.5', colors.border, colors.surface)}
              >
                <p className={cn('text-xs font-medium', colors.textSecondary)}>{s.label}</p>
                <p className={cn('mt-0.5 text-2xl font-semibold tabular-nums', colors.text)}>{s.value}</p>
                {s.sub ? <p className={cn('mt-0.5 text-xs', colors.textSecondary)}>{s.sub}</p> : null}
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      <Card
        variant="default"
        className={cn('border-0 p-4 shadow-sm ring-1 md:p-5', colors.borderLight, 'ring-inset')}
      >
        <Accordion className="space-y-3">
          <AccordionItem
            title={
              <div
                className={cn(
                  'w-full min-w-0 pr-1 text-left',
                  caseDetailsOpen
                    ? 'flex flex-col items-start gap-0'
                    : 'flex flex-wrap items-center gap-x-3 gap-y-2',
                )}
              >
                <span
                  className={cn(
                    'shrink-0 text-sm font-semibold tracking-tight',
                    colors.text,
                  )}
                >
                  Case details
                </span>
                {!caseDetailsOpen ? (
                  <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2">
                    {(
                      [
                        { k: 'Date', v: formatDate(item.caseDate) },
                        { k: 'ETA', v: formatDate(item.eta) },
                        { k: 'Vessel', v: item.vessel?.vesselName || '' },
                        { k: 'Status', v: item.status?.name || '' },
                        {
                          k: 'Port',
                          v: item.port
                            ? [item.port.name, item.port.code].filter(Boolean).join(' · ') || ''
                            : '',
                        },
                      ] as const
                    ).map((row) => (
                      <span
                        key={row.k}
                        className={cn(
                          'inline-flex max-w-full min-w-0 items-center gap-2 rounded-lg px-2.5 py-1.5',
                          'bg-black/[0.035] dark:bg-white/[0.06]',
                          'ring-1 ring-inset ring-black/[0.06] dark:ring-white/[0.08]',
                        )}
                        title={`${row.k}: ${row.v}`}
                      >
                        <span
                          className={cn(
                            'shrink-0 text-[0.65rem] font-semibold uppercase tracking-wider',
                            colors.textSecondary,
                          )}
                        >
                          {row.k}
                        </span>
                        <span
                          className="h-3.5 w-px shrink-0 bg-gray-300/80 dark:bg-gray-600"
                          aria-hidden
                        />
                        <span
                          className={cn(
                            'min-w-0 truncate text-xs font-medium leading-tight sm:text-sm',
                            colors.text,
                          )}
                        >
                          {row.v}
                        </span>
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            }
            open={caseDetailsOpen}
            onToggle={setCaseDetailsOpen}
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn(
              caseDetailsOpen ? '!items-start !gap-3' : '!items-center !gap-3',
              colors.surfaceHover,
            )}
          >
            <div
              className={cn(
                'rounded-xl p-4 sm:p-5',
                'bg-black/[0.02] dark:bg-white/[0.03]',
                'ring-1 ring-inset ring-black/[0.05] dark:ring-white/[0.08]',
              )}
            >
              <dl className="grid grid-cols-1 gap-x-8 gap-y-5 text-sm sm:grid-cols-2 lg:grid-cols-3">
                {(
                  [
                    {
                      k: 'Case date',
                      v: formatDate(item.caseDate),
                      col: '' as const,
                    },
                    { k: 'ETA', v: formatDate(item.eta), col: '' as const },
                    { k: 'Vessel', v: item.vessel?.vesselName || '', col: '' as const },
                    { k: 'Status', v: item.status?.name || '', col: '' as const },
                    {
                      k: 'Port',
                      v: item.port
                        ? [item.port.name, item.port.code].filter(Boolean).join(' · ') || ''
                        : '',
                      col: 'sm:col-span-2' as const,
                    },
                    {
                      k: 'Description',
                      v: item.description || '',
                      col: 'sm:col-span-2' as const,
                    },
                    {
                      k: 'Notes',
                      v: item.notes || '',
                      col: 'sm:col-span-2 lg:col-span-3' as const,
                    },
                  ] as const
                ).map((row) => (
                  <div key={row.k} className={cn('min-w-0', row.col)}>
                    <dt
                      className={cn(
                        'text-[0.7rem] font-semibold uppercase tracking-wider',
                        colors.textSecondary,
                      )}
                    >
                      {row.k}
                    </dt>
                    <dd
                      className={cn(
                        'mt-2 text-sm font-medium leading-snug sm:leading-relaxed',
                        row.k === 'Description' || row.k === 'Notes' ? 'font-normal' : undefined,
                        colors.text,
                      )}
                    >
                      {row.v}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </AccordionItem>
        </Accordion>
      </Card>

      <div className="grid gap-6 lg:grid-cols-1">
        <Card
          variant="default"
          className={cn('overflow-hidden border-0 p-0 shadow-sm ring-1', colors.borderLight, 'ring-inset')}
        >
            <div className={cn('border-b px-4 py-3 md:px-5', colors.border, colors.surfaceHover)}>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-1">
                <SegmentedControl
                  size="sm"
                  aria-label="Case view sections"
                  value={viewTab}
                  onChange={setViewTab}
                  options={[
                    { id: 'case', label: 'Case activity' },
                    { id: 'items', label: 'Items activity' },
                  ]}
                />
              </div>
              <div className="flex shrink-0 items-center gap-0.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-9 shrink-0 gap-1.5"
                  onClick={() => void refreshSpareCaseCaches()}
                  title="Refresh this case and related lists"
                >
                  <RefreshIcon className="h-4 w-4" />
                  Refresh
                </Button>
                {viewTab === 'case' ? (
                  <button
                    type="button"
                    className={cn(
                      'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg outline-none transition-colors',
                      colors.textSecondary,
                      'hover:bg-slate-200/80 hover:text-slate-800 dark:hover:bg-slate-700/80 dark:hover:text-slate-100',
                      'focus-visible:ring-2 focus-visible:ring-slate-400/80 focus-visible:ring-offset-1',
                    )}
                    title={CASE_ACTIVITY_INFO_TOOLTIP}
                    aria-label="About case activity: pipeline steps, references in details, table versus stages, and row actions"
                  >
                    <InfoIcon className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
            </div>
            <div>
              {viewTab === 'items' ? (
                <p className={cn('mt-1 text-xs', colors.textSecondary)}>
                  One row per requisition line (matched across documents by item). Columns follow the pipeline:
                  supplier enquiries, quotations, orders, deliveries, and invoices. Scroll horizontally when there
                  are many documents; header rows stay visible while you scroll the grid vertically.
                </p>
              ) : null}
            </div>
            {viewTab === 'case' ? (
              <>
                <div
                  className={cn(
                    'mt-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2',
                    colors.border,
                    'border-t pt-3',
                  )}
                >
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <Link
                      to={newRequisitionTo}
                      className={cn(
                        'inline-flex h-8 items-center justify-center rounded-lg border px-3 py-1.5 text-xs font-medium shadow-sm',
                        colors.border,
                        colors.text,
                      )}
                    >
                      New requisition
                    </Link>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8"
                      onClick={() => {
                        setImportSelectedId('');
                        setImportReqOpen(true);
                      }}
                    >
                      Import requisition…
                    </Button>
                    {data?.requisitions && data.requisitions.length > 1 && mergeableRequisitions.length > 1 ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className={cn(
                          'h-8 gap-1.5 border-emerald-200/80 text-emerald-900 hover:bg-emerald-50',
                          'dark:border-emerald-800/80 dark:text-emerald-100 dark:hover:bg-emerald-950/40',
                        )}
                        onClick={() => {
                          setCombineReqIds(mergeableRequisitions.map((r) => String(r.id)));
                          setCombineReqOpen(true);
                        }}
                      >
                        <DocumentTextIcon className="h-3.5 w-3.5 shrink-0 opacity-90" />
                        Merge requisitions
                      </Button>
                    ) : null}
                  </div>
                  <SegmentedControl
                    size="sm"
                    aria-label="Activity layout"
                    value={viewMode}
                    onChange={setViewMode}
                    options={[
                      {
                        id: 'flat',
                        label: 'Table',
                        icon: <TableIcon className="h-3.5 w-3.5" />,
                      },
                      {
                        id: 'grouped',
                        label: 'Stages',
                        icon: <ViewColumnsIcon className="h-3.5 w-3.5" />,
                      },
                    ]}
                  />
                </div>
                {viewMode === 'grouped' && stageGroups.length > 0 ? (
                  <nav
                    className={cn('mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5 border-t pt-2', colors.border)}
                    aria-label="Jump to pipeline stage"
                  >
                    <span className={cn('text-xs font-medium', colors.textSecondary)}>Jump to</span>
                    {stageGroups.map((g) => {
                      const stageAnchor = `case-stage-${g.kind.replace(/\s+/g, '-').toLowerCase()}`;
                      return (
                        <a
                          key={g.kind}
                          href={`#${stageAnchor}`}
                          className={cn(
                            'rounded-md text-xs font-semibold text-indigo-600 underline-offset-2 hover:underline dark:text-indigo-400',
                          )}
                        >
                          {g.kind}
                        </a>
                      );
                    })}
                  </nav>
                ) : null}
              </>
            ) : null}
          </div>
          <div className="overflow-x-auto p-0">
            {viewTab === 'items' ? (
              data?.itemsActivity ? (
                <CaseItemsActivityTable payload={data.itemsActivity} />
              ) : (
                <p className={cn('px-4 py-8 text-sm', colors.textSecondary, 'md:px-5')}>
                  No items data (reload or refresh the page).
                </p>
              )
            ) : viewMode === 'flat' ? (
              <table className="min-w-full text-sm">
                <thead className={cn('sticky top-0 z-20 shadow-sm', colors.surface)}>
                  <tr className={cn('text-left', colors.border, 'border-b')}>
                    <th className={cn('px-4 py-2.5 text-xs font-medium uppercase tracking-wide', colors.textSecondary, 'md:pl-5')}>
                      Type
                    </th>
                    <th
                      className={cn(
                        'py-2.5 pl-0 pr-2 text-left text-xs font-medium uppercase tracking-wide',
                        colors.textSecondary,
                      )}
                      title="Pipeline actions (e.g. create enquiry, quotation, order, delivery, invoice)"
                    >
                      Action
                    </th>
                    <th className={cn('px-2 py-2.5 text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>
                      Reference
                    </th>
                    <th
                      className={cn(
                        'whitespace-nowrap px-2 py-2.5 text-xs font-medium uppercase tracking-wide',
                        colors.textSecondary,
                      )}
                    >
                      Date
                    </th>
                    <th
                      className={cn(
                        'min-w-[18rem] px-2 py-2.5 text-xs font-medium uppercase tracking-wide sm:min-w-[24rem] lg:min-w-[32rem]',
                        colors.textSecondary,
                      )}
                    >
                      Details
                    </th>
                    <th
                      className={cn(
                        'w-[5.5rem] min-w-[5.5rem] py-2.5 pr-4 pl-0 text-right text-xs font-medium uppercase tracking-wide md:pr-5',
                        colors.textSecondary,
                      )}
                      title="Copy row summary or open the record"
                    >
                      Open
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {allRows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className={cn('px-4 py-8 text-center text-sm', colors.textSecondary, 'md:px-5')}>
                        No related records for this case
                      </td>
                    </tr>
                  ) : sortedRows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className={cn('px-4 py-8 text-center text-sm', colors.textSecondary, 'md:px-5')}>
                        No records match the current filter
                      </td>
                    </tr>
                  ) : (
                    sortedRows.map((row) => renderActivityRow(row))
                  )}
                </tbody>
              </table>
            ) : (
              <div className="px-4 py-4 md:px-5 md:py-5">
                {allRows.length === 0 ? (
                  <p className={cn('text-sm', colors.textSecondary)}>No related records for this case</p>
                ) : stageGroups.length === 0 ? (
                  <p className={cn('text-sm', colors.textSecondary)}>No records match the current filter</p>
                ) : (
                  <div className={cn('overflow-hidden rounded-xl border shadow-sm', colors.border, colors.surface)}>
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[52rem] table-fixed border-collapse text-sm">
                        {caseActivityGroupedColgroup}
                        <thead className={cn('sticky top-0 z-10 shadow-sm', colors.surface)}>
                          <tr className={cn('text-left', colors.border, 'border-b')}>
                            <th
                              className={cn(
                                'px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide md:pl-5',
                                colors.textSecondary,
                              )}
                              title="Pipeline actions (e.g. create enquiry, quotation, order, delivery, invoice)"
                            >
                              Action
                            </th>
                            <th
                              className={cn(
                                'px-2 py-2.5 text-xs font-medium uppercase tracking-wide',
                                colors.textSecondary,
                              )}
                            >
                              Reference
                            </th>
                            <th
                              className={cn(
                                'whitespace-nowrap px-2 py-2.5 text-xs font-medium uppercase tracking-wide',
                                colors.textSecondary,
                              )}
                            >
                              Date
                            </th>
                            <th
                              className={cn(
                                'px-2 py-2.5 text-xs font-medium uppercase tracking-wide',
                                colors.textSecondary,
                              )}
                            >
                              Details
                            </th>
                            <th
                              className={cn(
                                'w-[5.5rem] py-2.5 pr-4 pl-0 text-right text-xs font-medium uppercase tracking-wide md:pr-5',
                                colors.textSecondary,
                              )}
                              title="Copy row summary or open the record"
                            >
                              Open
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {stageGroups.map((g, idx) => {
                            const stageAnchor = `case-stage-${g.kind.replace(/\s+/g, '-').toLowerCase()}`;
                            const countLabel = `${g.rows.length} record${g.rows.length === 1 ? '' : 's'}`;
                            return (
                              <React.Fragment key={g.kind}>
                                <tr className={cn(colors.border, idx > 0 ? 'border-t' : '')}>
                                  <td colSpan={5} className={cn('scroll-mt-4 p-0', idx === 0 ? 'pt-3 pb-2' : 'pt-8 pb-2')}>
                                    <div className="px-4 md:px-5">
                                      <div
                                        id={stageAnchor}
                                        className={cn(
                                          'flex flex-wrap items-center justify-between gap-2 rounded-xl border px-3 py-2.5 sm:px-4',
                                          colors.border,
                                          colors.surface,
                                          activityDetailsRailClass(g.kind),
                                        )}
                                      >
                                        <span className={kindBadgeClass(g.kind)}>{g.kind}</span>
                                        <span
                                          className={cn(
                                            'tabular-nums text-[11px] font-semibold tracking-wide',
                                            colors.textSecondary,
                                          )}
                                        >
                                          {countLabel}
                                        </span>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                                {g.rows.map((row) => renderActivityRow(row, { grouped: true }))}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>
      </div>

      <Modal
        isOpen={enquiryOpen}
        onClose={() => {
          if (!enquiryBusy) setEnquiryOpen(false);
        }}
        size="lg"
        zIndex={120}
      >
        <ModalHeader>
          <ModalTitle>New supplier enquiries</ModalTitle>
          {enquiryReqLabel ? (
            <p className={cn('mt-0.5 text-sm', colors.textSecondary)}>From requisition {enquiryReqLabel}</p>
          ) : null}
        </ModalHeader>
        <ModalContent>
          <div className="max-h-[68vh] space-y-4 overflow-y-auto pr-1">
            <p className={cn('text-sm leading-relaxed', colors.textSecondary)}>
              Header and lines are copied from the requisition. Select one or more suppliers; one enquiry is
              created per supplier, all linked to this case.
            </p>
            {enquiryReqSaveStatus === 'DRAFT' ? (
              <div
                className={cn(
                  'rounded-lg border px-3 py-2.5 text-sm',
                  'border-amber-300 bg-amber-50 text-amber-950',
                  'dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100',
                )}
                role="status"
              >
                This requisition is still a <span className="font-semibold">draft</span>. You can create
                enquiries now, or save the requisition first if you need to persist line changes.
              </div>
            ) : null}
            <div>
              <p className={cn('mb-1.5 text-xs font-medium', colors.textSecondary)}>After creating</p>
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                <Button
                  type="button"
                  size="sm"
                  variant={postCreate === 'first' ? 'default' : 'outline'}
                  onClick={() => setPostCreate('first')}
                  disabled={enquiryBusy}
                >
                  Open first enquiry
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={postCreate === 'stay' ? 'default' : 'outline'}
                  onClick={() => setPostCreate('stay')}
                  disabled={enquiryBusy}
                >
                  Stay on this case
                </Button>
              </div>
            </div>
            <div>
              <Label id="case-view-enq-suppliers-label" htmlFor="case-view-enq-suppliers">
                Suppliers
              </Label>
              <MultiSelect
                id="case-view-enq-suppliers"
                options={supplierMultiOptions}
                value={enquirySupplierIds}
                onChange={setEnquirySupplierIds}
                placeholder="Select one or more suppliers…"
                maxDisplay={3}
                className="mt-1.5"
                disabled={enquiryBusy}
              />
            </div>
            <div>
              <Label htmlFor="case-view-enq-notes">Notes (optional)</Label>
              <Textarea
                id="case-view-enq-notes"
                value={enquiryNotes}
                onChange={(e) => setEnquiryNotes(e.target.value)}
                rows={3}
                showClearButton={false}
                placeholder="Override requisition notes on the new enquiry"
                className="min-h-[88px]"
                disabled={enquiryBusy}
              />
            </div>
          </div>
        </ModalContent>
        <ModalFooter>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={enquiryBusy}
            onClick={() => setEnquiryOpen(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            loading={enquiryBusy}
            disabled={enquiryRequisitionId == null || enquirySupplierIds.length === 0}
            onClick={async () => {
              if (enquiryRequisitionId == null) return;
              const parsed = enquirySupplierIds
                .map((v) => Number(v))
                .filter((n) => Number.isFinite(n)) as number[];
              const supplierIds: number[] = [];
              for (const id of parsed) {
                if (supplierIds.indexOf(id) === -1) supplierIds.push(id);
              }
              if (supplierIds.length === 0) return;
              setEnquiryBusy(true);
              try {
                const created = await createSpareSupplierEnquiryFromRequisitionMulti({
                  requisitionId: enquiryRequisitionId,
                  supplierIds,
                  ...(enquiryNotes.trim() ? { notes: enquiryNotes.trim() } : {}),
                });
                setEnquiryOpen(false);
                setEnquiryRequisitionId(null);
                setEnquirySupplierIds([]);
                await revalidateCaseView();
                const n = created.length;
                if (n > 0) {
                  showSuccess(
                    n > 1 ? `${n} supplier enquiries created.` : 'Supplier enquiry created.',
                  );
                }
                const firstId = created[0]?.id;
                if (postCreate === 'first' && firstId != null) {
                  navigate(supplierEnquiryConfig.module.route.detail(firstId));
                }
              } catch (err: unknown) {
                const data =
                  err && typeof err === 'object' && 'response' in err
                    ? (err as { response?: { data?: { message?: string | string[] } } }).response?.data
                    : undefined;
                const m = data?.message;
                const text = Array.isArray(m)
                  ? m.join('; ')
                  : typeof m === 'string'
                    ? m
                    : 'Could not create supplier enquiry.';
                showError(text);
              } finally {
                setEnquiryBusy(false);
              }
            }}
          >
            {enquirySupplierIds.length <= 1
              ? 'Create enquiry'
              : `Create ${enquirySupplierIds.length} enquiries`}
          </Button>
        </ModalFooter>
      </Modal>

      <ConfirmationDialog
        isOpen={deleteConfirmRow != null}
        onClose={() => {
          if (deletingRowKey == null) setDeleteConfirmRow(null);
        }}
        onConfirm={() => {
          if (!deleteConfirmRow) return;
          void performDelete(deleteConfirmRow);
        }}
        title={
          deleteConfirmRow?.kind === 'Requisition'
            ? 'Remove requisition from case'
            : `Delete ${deleteConfirmRow?.kind ?? 'record'}`
        }
        message={
          deleteConfirmRow
            ? deleteConfirmRow.kind === 'Requisition'
              ? `Remove ${deleteConfirmRow.ref} from this case only? The requisition remains in the requisitions module with its lines.`
              : `Are you sure you want to delete ${deleteLabelForKind(deleteConfirmRow.kind)} ${deleteConfirmRow.ref}?`
            : 'Are you sure you want to delete this record?'
        }
        warningMessage={
          deleteConfirmRow?.kind === 'Requisition'
            ? 'This does not delete the requisition record.'
            : 'This action cannot be undone.'
        }
        details={
          deleteConfirmRow
            ? {
                Type: deleteConfirmRow.kind,
                Reference: deleteConfirmRow.ref,
              }
            : undefined
        }
        confirmText={
          deletingRowKey != null
            ? deleteConfirmRow?.kind === 'Requisition'
              ? 'Removing…'
              : 'Deleting…'
            : deleteConfirmRow?.kind === 'Requisition'
              ? 'Remove from case'
              : 'Delete'
        }
        cancelText="Cancel"
        confirmVariant={deleteConfirmRow?.kind === 'Requisition' ? 'default' : 'danger'}
        isLoading={deletingRowKey != null}
      />

      <Modal
        isOpen={combineReqOpen}
        onClose={() => {
          if (!combineReqBusy) setCombineReqOpen(false);
        }}
        size="lg"
        zIndex={120}
      >
        <ModalHeader>
          <div className="flex w-full min-w-0 items-start gap-3">
            <div
              className={cn(
                'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border shadow-sm',
                colors.border,
                'bg-emerald-50 dark:bg-emerald-950/45',
              )}
              aria-hidden
            >
              <DocumentTextIcon className="h-5 w-5 text-emerald-700 dark:text-emerald-300" />
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              <ModalTitle>Merge requisitions</ModalTitle>
              <p className={cn('text-sm font-normal leading-snug', colors.textSecondary)}>
                Create one draft requisition on this case
                {item.ref ? (
                  <>
                    {' '}
                    <span className="font-medium text-emerald-800 dark:text-emerald-200">({item.ref})</span>
                  </>
                ) : null}
                . Requisitions with a purchase order on this case cannot be merged.
              </p>
            </div>
          </div>
        </ModalHeader>
        <ModalContent>
          <div className="space-y-5">
            <div
              className={cn(
                'flex gap-3 rounded-xl border px-3.5 py-3 text-sm leading-relaxed shadow-sm',
                'border-emerald-200/90 bg-emerald-50 text-emerald-950',
                'dark:border-emerald-800/90 dark:bg-emerald-950/35 dark:text-emerald-50',
              )}
              role="status"
            >
              <InfoIcon className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700 dark:text-emerald-300" />
              <div className="space-y-2">
                <p>
                  <span className="font-semibold">Purchase order:</span> merging is only allowed while{' '}
                  <span className="font-medium">no purchase order</span> exists for any selected requisition on this case
                  (order → delivery → invoice blocks merge).
                </p>
                <p>
                  <span className="font-semibold">Supplier enquiry (RFQ):</span> if exactly one selected requisition
                  already has RFQs, lines from the others are <span className="font-medium">imported into that</span>{' '}
                  requisition (nothing new is created). Otherwise a <span className="font-medium">new draft</span>{' '}
                  requisition is created and holds all merged lines.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className={cn('text-xs font-semibold uppercase tracking-wide', colors.textSecondary)}>
                  Include in merge
                </span>
                <div className="flex flex-wrap gap-1.5">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    disabled={combineReqBusy}
                    onClick={() => setCombineReqIds(mergeableRequisitions.map((r) => String(r.id)))}
                  >
                    Select all
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs"
                    disabled={combineReqBusy}
                    onClick={() => setCombineReqIds([])}
                  >
                    Clear
                  </Button>
                </div>
              </div>

              <div className={cn('overflow-hidden rounded-xl border shadow-sm', colors.border)}>
                <ul
                  className={cn(
                    'max-h-[min(320px,46vh)] divide-y overflow-y-auto overscroll-contain',
                    colors.border,
                  )}
                >
                  {(data?.requisitions ?? []).map((r) => {
                    const idStr = String(r.id);
                    const hasPo = requisitionIdsWithPurchaseOrder.has(r.id);
                    const checked = !hasPo && combineReqIds.some((v) => String(v) === idStr);
                    const refLabel = r.ref?.trim() || `Requisition #${r.id}`;
                    const dateLabel = formatDate(r.reqDate);
                    const statusLabel = r.saveStatus?.trim();
                    const enqN = enquiryCountByRequisitionId.get(r.id) ?? 0;
                    return (
                      <li key={r.id}>
                        <div
                          className={cn(
                            'flex items-center justify-between gap-3 px-4 py-3.5 transition-colors',
                            checked ? cn(colors.surfaceHover, 'bg-emerald-50/60 dark:bg-emerald-950/25') : colors.surfaceHover,
                            hasPo && 'opacity-60',
                            combineReqBusy && 'pointer-events-none opacity-60',
                          )}
                        >
                          <span className="min-w-0 flex-1 pr-1">
                            <span className={cn('flex flex-wrap items-center gap-2', colors.text)}>
                              <span className="font-medium leading-tight">{refLabel}</span>
                              {hasPo ? (
                                <span
                                  className={cn(
                                    'rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                                    'border border-rose-200 bg-rose-50 text-rose-900',
                                    'dark:border-rose-900 dark:bg-rose-950/50 dark:text-rose-100',
                                  )}
                                >
                                  Has order
                                </span>
                              ) : null}
                              {enqN > 0 ? (
                                <span
                                  className={cn(
                                    'rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                                    colors.border,
                                    colors.surface,
                                  )}
                                >
                                  RFQ ×{enqN}
                                </span>
                              ) : null}
                            </span>
                            <span className={cn('mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-xs', colors.textSecondary)}>
                              {dateLabel ? <span>{dateLabel}</span> : null}
                              {statusLabel ? (
                                <span
                                  className={cn(
                                    'rounded-md px-1.5 py-0.5 font-medium',
                                    colors.border,
                                    colors.surface,
                                  )}
                                >
                                  {statusLabel}
                                </span>
                              ) : null}
                            </span>
                          </span>
                          <Switch
                            checked={checked}
                            disabled={combineReqBusy || hasPo}
                            size="sm"
                            onChange={(e) => {
                              if (hasPo) return;
                              const on = e.target.checked;
                              setCombineReqIds((prev) => {
                                if (on) {
                                  if (prev.some((v) => String(v) === idStr)) return prev;
                                  return [...prev, idStr];
                                }
                                return prev.filter((v) => String(v) !== idStr);
                              });
                            }}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        </ModalContent>
        <ModalFooter className="flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className={cn('text-center text-xs sm:text-left', colors.textSecondary)}>
            {combineReqIds.length < 2
              ? 'Choose at least two requisitions to merge.'
              : combineSubmitMode === 'blockedMultipleRfq'
                ? 'More than one selection already has supplier enquiries. Turn off switches until only one RFQ-linked requisition stays included.'
                : combineSubmitMode === 'importIntoRfq'
                  ? 'Lines from the other requisitions are imported into the RFQ-linked one; each source is unlinked from the case and its lines are cleared (the requisition record remains).'
                  : `${combineReqIds.length} selected — a new saved requisition will hold all merged lines.`}
          </p>
          <div className="flex w-full justify-end gap-2 sm:w-auto">
            <Button type="button" variant="ghost" onClick={() => setCombineReqOpen(false)} disabled={combineReqBusy}>
              Cancel
            </Button>
            <Button
              type="button"
              loading={combineReqBusy}
              disabled={combineReqIds.length < 2 || combineSubmitMode === 'blockedMultipleRfq'}
              onClick={async () => {
                if (!item?.id) return;
                const selectedIds = Array.from(
                  new Set(
                    combineReqIds
                      .map((v) => Number(v))
                      .filter((n) => Number.isFinite(n) && n > 0),
                  ),
                ).sort((a, b) => a - b);
                if (selectedIds.length < 2) return;
                setCombineReqBusy(true);
                try {
                  const blockedPo = selectedIds.filter((id) => requisitionIdsWithPurchaseOrder.has(id));
                  if (blockedPo.length > 0) {
                    showError(
                      'Cannot merge while a purchase order exists for a selected requisition. Exclude requisitions that already have an order on this case.',
                    );
                    return;
                  }
                  const withEnqIds = selectedIds.filter((id) => (enquiryCountByRequisitionId.get(id) ?? 0) > 0);
                  if (withEnqIds.length > 1) {
                    showError('Only one requisition may already have supplier enquiries.');
                    return;
                  }

                  type ReqLine = {
                    itemId?: number | null;
                    itemVcode?: string | null;
                    itemDescr?: string | null;
                    unitId?: number | null;
                    qty?: number | null;
                    rob?: number | null;
                    itemNotes?: string | null;
                  };
                  type ReqDetail = {
                    id: number;
                    ref?: string | null;
                    reqDate?: string | null;
                    caseId?: number | null;
                    vesselId?: number | null;
                    categoryIds?: number[];
                    notes?: string | null;
                    lines?: ReqLine[];
                  };

                  const mapLineToPayload = (line: ReqLine) => ({
                    itemId: line.itemId ?? undefined,
                    itemVcode: line.itemVcode ?? undefined,
                    itemDescr: line.itemDescr ?? undefined,
                    unitId: line.unitId ?? undefined,
                    qty: line.qty ?? undefined,
                    rob: line.rob ?? undefined,
                    itemNotes: line.itemNotes ?? undefined,
                  });

                  const details = await Promise.all(
                    selectedIds.map((rid) => fetcher(`/spare-requisitions/${rid}`) as Promise<ReqDetail>),
                  );

                  if (withEnqIds.length === 1) {
                    const targetId = withEnqIds[0];
                    const targetDetail = details.find((d) => d.id === targetId);
                    if (!targetDetail) throw new Error('Target requisition not found');
                    const sourceIds = selectedIds.filter((id) => id !== targetId);
                    const mergedLines = [
                      ...(targetDetail.lines ?? []).map(mapLineToPayload),
                      ...sourceIds.flatMap((sid) => {
                        const d = details.find((x) => x.id === sid);
                        return (d?.lines ?? []).map(mapLineToPayload);
                      }),
                    ];
                    const noteParts: string[] = [];
                    const tn = (targetDetail.notes ?? '').trim();
                    if (tn) noteParts.push(tn);
                    for (const sid of sourceIds) {
                      const d = details.find((x) => x.id === sid);
                      if (!d) continue;
                      const n = (d.notes ?? '').trim();
                      if (!n) continue;
                      const tag = d.ref?.trim() || `R-${sid}`;
                      noteParts.push(`[from ${tag}] ${n}`);
                    }
                    await updateSpareRequisition(targetId, {
                      lines: mergedLines,
                      notes: noteParts.length ? noteParts.join('\n\n') : undefined,
                    });
                    for (const sid of sourceIds) {
                      await updateSpareRequisition(sid, { caseId: null, lines: [] });
                    }
                    setCombineReqOpen(false);
                    setCombineReqIds([]);
                    await revalidateCaseView();
                    showSuccess('Lines imported into the RFQ-linked requisition.');
                    navigate(requisitionsModuleConfig.module.route.edit(targetId));
                  } else {
                    const base = details[0];
                    const mergedLines = details.flatMap((req) =>
                      (req.lines ?? []).map((line) => mapLineToPayload(line)),
                    );
                    const mergedNotes = details
                      .map((req) => {
                        const n = (req.notes ?? '').trim();
                        if (!n) return '';
                        const tag = req.ref?.trim() || `R-${req.id}`;
                        return `[${tag}] ${n}`;
                      })
                      .filter(Boolean)
                      .join('\n\n');
                    const created = await createSpareRequisition({
                      reqDate: base?.reqDate ? String(base.reqDate).slice(0, 10) : getTodayLocalDateString(),
                      caseId: item.id,
                      vesselId: base?.vesselId ?? item.vessel?.id ?? undefined,
                      categoryIds: base?.categoryIds?.length ? [...base.categoryIds] : undefined,
                      ref: `COMBINED-${Date.now()}`,
                      notes: mergedNotes || undefined,
                      lines: mergedLines,
                      saveStatus: 'SAVED',
                    });
                    setCombineReqOpen(false);
                    setCombineReqIds([]);
                    await revalidateCaseView();
                    showSuccess('Merged requisition created.');
                    if (created?.id != null) {
                      navigate(requisitionsModuleConfig.module.route.edit(created.id));
                    }
                  }
                } catch (err: unknown) {
                  const errData =
                    err && typeof err === 'object' && 'response' in err
                      ? (err as { response?: { data?: { message?: string | string[] } } }).response?.data
                      : undefined;
                  const m = errData?.message;
                  const text = Array.isArray(m)
                    ? m.join('; ')
                    : typeof m === 'string'
                      ? m
                      : 'Could not merge requisitions.';
                  showError(text);
                } finally {
                  setCombineReqBusy(false);
                }
              }}
              className="bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-500"
            >
              {combineSubmitMode === 'importIntoRfq' ? 'Import into RFQ requisition' : 'Merge into saved requisition'}
            </Button>
          </div>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={importReqOpen}
        onClose={() => {
          setImportReqOpen(false);
          setImportSelectedId('');
        }}
        size="md"
        zIndex={120}
      >
        <ModalHeader>
          <ModalTitle>Import requisition</ModalTitle>
        </ModalHeader>
        <ModalContent>
          {!vesselIdForImport ? (
            <p className={cn('text-sm', colors.textSecondary)}>
              This case has no vessel. Assign a vessel on the case to import lines from requisitions on the same vessel
              (no case, with lines).
            </p>
          ) : importCandidatesLoading ? (
            <p className={cn('text-sm', colors.textSecondary)}>Loading requisitions…</p>
          ) : importCandidatesSameVessel.length === 0 ? (
            <p className={cn('text-sm', colors.textSecondary)}>
              No requisitions found. They must be saved (not draft), have no case, the same vessel as this case, and at
              least one line.
            </p>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="caseview-import-req">Requisition to import</Label>
              <Select
                id="caseview-import-req"
                name="importRequisition"
                value={importSelectedId}
                onChange={(e) => setImportSelectedId(e.target.value)}
                options={[
                  { value: '', label: 'Choose a requisition…' },
                  ...importCandidatesSameVessel.map((r) => ({
                    value: String(r.id),
                    label: `${r.reqDate ? formatDate(r.reqDate) : '—'} - ${(r.ref || '').trim() || `#${r.id}`}`,
                  })),
                ]}
                showClearButton={false}
              />
            </div>
          )}
        </ModalContent>
        <ModalFooter>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={importBusy}
            onClick={() => {
              setImportReqOpen(false);
              setImportSelectedId('');
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={
              importBusy ||
              !importSelectedId ||
              !vesselIdForImport ||
              importCandidatesSameVessel.length === 0 ||
              importCandidatesLoading
            }
            onClick={async () => {
              if (!importSelectedId) return;
              const reqId = Number(importSelectedId);
              if (!Number.isFinite(reqId)) return;
              const selectedRequisition = importCandidatesSameVessel.find((r) => r.id === reqId);
              const selectedVesselId =
                selectedRequisition?.vesselId != null ? Number(selectedRequisition.vesselId) : Number(vesselIdForImport);
              if (!Number.isFinite(selectedVesselId) || selectedVesselId <= 0) {
                showError('Could not link requisition: missing requisition vessel.');
                return;
              }
              try {
                setImportBusy(true);
                await updateSpareRequisition(reqId, {
                  caseId: item.id,
                  vesselId: selectedVesselId,
                });
                await revalidateCaseView();
                showSuccess('Requisition linked to this case.');
                setImportReqOpen(false);
                setImportSelectedId('');
              } catch (err: unknown) {
                const dataErr =
                  err && typeof err === 'object' && 'response' in err
                    ? (err as { response?: { data?: { message?: string | string[] } } }).response?.data
                    : undefined;
                const m = dataErr?.message;
                const text = Array.isArray(m) ? m.join('; ') : typeof m === 'string' ? m : 'Could not link requisition.';
                showError(text);
              } finally {
                setImportBusy(false);
              }
            }}
          >
            Continue
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default CaseView;
